import "./style.css";


import type { User } from "@supabase/supabase-js";
import { AuthManager, type AuthState } from "./auth/authManager";
import { resolveAdminDebugAccess } from "./auth/adminDebugAccess";
import { AuthOverlay } from "./auth/authOverlay";
import { BootOverlayManager, type BootStartResult } from "./boot/bootOverlayManager";
import { PostLoginDisclaimerManager } from "./boot/postLoginDisclaimerManager";

import { AudioManager } from "./game/audio";
import { InputManager, type Rect, type UiAction, type UiRects } from "./game/input";
import { getSettingsPageDefinition, type SettingsSurface } from "./game/settingsPages";
import { GameController } from "./game/stateMachine";
import { RAFSBY_NPC, RAFSBY_BOT_IDENTITY } from "./game/onboarding";
import { hapticManager } from "./game/haptics";
import { networkManager } from "./game/network";
import { loadDebugSettings, loadRunState, loadSettings, saveDebugSettings, saveSettings, hasOnboardingBeenSeen, markOnboardingSeen, resetOnboardingSeen } from "./persistence/save";
import { saveRunStateCloud } from "./persistence/cloudSave";
import { CanvasRenderer } from "./render/canvasRenderer";
import { AssetManager } from "./render/assets/assetManager";
import { LobbyManager, generateLobbyCode } from "./versus/lobbyManager";
import { LobbyRealtime } from "./versus/lobbyRealtime";
import { runVersusMenuAction, type VersusMenuActionState } from "./versus/menuActionRunner";
import { quickmatch } from "./versus/versusMatchmaker";
import { createMatch, fetchRankedContext, fetchRankedLeaderboard, finalizeMatch, submitMatchSummary, updateBotMatchRating } from "./versus/versusDb";
import { rankedTierForRating, type LeaderboardTab, type LobbyQueueType } from "./versus/ranked";
import { seedFromTime } from "./game/rng";
import {
  fetchBalance,
  creditCoins,
  creditCoinsLocal,
  creditGems,
  creditGemsLocal,
  deductGemsLocal,
  exchangeCoinsToGems,
  exchangeCoinsToGemsLocal,
  getCachedBalance,
  getCachedDailyLoginBonusStatus,
  buyGemPackLocal,
  buyGemPack,
  fetchDailyLoginBonusStatus,
  claimDailyLoginBonus,
  shouldAutoOpenDailyLoginBonus,
  markDailyLoginBonusDismissed,
  clearDailyLoginBonusDismissal,
  claimFirstWinBonus,
  claimFirstWinBonusLocal,
  canClaimFirstWinBonus,
  resetWalletCache,
  RETENTION_REWARD_TABLE
} from "./economy/walletManager";
import { fetchInventory, fetchEquipped, purchaseCharacter, equipCharacter, equipCharacterLocal, addOwnedLocal, getCachedEquippedCharacter, getCachedOwnedIds, primeCachedEquipped, resetInventoryCache } from "./economy/inventoryManager";
import { GEM_PACKS, getCharacterById, type CharacterDef } from "./economy/characters";
import { currentUtcDateString, getDailyLoginReward, type DailyLoginBonusStatus } from "./economy/dailyLoginRewards";
import { FIRST_WIN_DAILY_XP_BONUS, addXp, featureUnlockLevel, isFeatureUnlocked, levelFromTotalXp, isSoloRunXpEligible, soloRunXp, versusRunXp } from "./economy/levelSystem";
import { iapManager } from "./economy/iapManager";
import { getIapPackByProductId } from "./economy/iapProducts";
import { FriendManager } from "./social/friendManager";
import { MessageService } from "./social/messageService";
import { BotPlayer } from "./versus/botPlayer";
import type { BotIdentity } from "./versus/botIdentity";
import type { RenderModel } from "./render/renderModel";
import { createPowerupRadialItems, createTrapRadialItems, resolveHighlightedIndex, RADIAL_HOLD_THRESHOLD_MS, type RadialMenuState } from "./game/radialMenu";
import type { LobbyChatMessage, LobbyData, LobbySettings, MatchResult, VersusLobbyRenderData, VersusMatchRenderData, VersusResultData } from "./versus/types";
import type { IapPurchaseState, PerformanceSettings } from "./game/types";
import { VersusController } from "./versus/versusController";
import { saveActiveMatch, loadActiveMatch, clearActiveMatch, saveMatchSnapshot, loadMatchSnapshot } from "./versus/matchSessionStore";
import { MatchRealtimeSync } from "./versus/realtimeSync";
import { VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT, VERSUS_MOBILE_CANVAS_WIDTH } from "./versus/versusConstants";
import { computeCanvasDimensions } from "./game/constants";
import { isMobileViewport } from "./game/platform";
import { createQuickmatchAttemptGuard } from "./versus/quickmatchAttemptGuard";
import { shouldFallbackToBot } from "./versus/quickmatchRouting";
import { createBotSkillProfile } from "./versus/botSkillProfile";
import type { AdminDebugAccess } from "./auth/adminDebugAccess";
import type { DebugSettings } from "./game/debugSettings";
import { walletService } from "./wallet/walletService";
import { solanaService } from "./wallet/solanaService";
import { tokenShopService } from "./wallet/tokenShopService";
import { resolveAvatarList, isCacheStale, formatSyncAge } from "./wallet/avatarResolver";
import { shouldBlockEdgeSwipeStart } from "./game/edgeSwipeGuard";
import { adService } from "./ads/adService";
import { referralService } from "./referral/referralService";
import { pwaInstallPrompt } from "./pwa/installPrompt";
import { pushService } from "./push/pushService";
import { staminaService } from "./stamina/staminaService";
import { getSelectedProfileVisibleItem } from "./game/profileVisibleItems";
import { modesNewlyUnlocked, SOLO_MODES } from "./game/soloModes";

// Toggle this to bypass Supabase auth for local development.
// Also skipped when VITE_E2E_GUEST=true (set by Playwright e2e tests).
const FORCE_SKIP_AUTH = false || import.meta.env.VITE_E2E_GUEST === "true";
const FRIEND_REMOVE_PROMPTS = [
  "Are you sure you wanna can this friendship?",
  "Are you sure you never wanna talk to this person again?",
  "Are you sure you wanna ghost them?"
] as const;

function hasSupabaseEnv(): boolean {
  const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
  const key = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim();
  return Boolean(url && key);
}

function isPersistentAccount(user: User | null): boolean {
  return user != null && user.id !== "guest-local";
}

function createGuestAuthManager(): AuthManager {
  const guestUser = { id: "guest-local" } as unknown as User;
  let state: AuthState = {
    user: guestUser,
    profile: {
      id: "guest-local",
      displayName: "PLAYER",
      equippedCharacter: "skin_08118",
      avatarUrl: null,
      provider: "guest",
      versusWins: 0,
      versusLosses: 0,
      versusPlayed: 0,
      versusElo: 1000,
      rankedWins: 0,
      rankedLosses: 0,
      rankedGames: 0,
      rankedDisconnects: 0,
      rankedPeakRating: 1000,
      rankedLastMatchAt: null,
      username: "guest",
      level: 1,
      totalXp: 0
    },
    loading: false
  };

  const listeners: Array<(authState: AuthState) => void> = [];
  const notify = (): void => {
    for (const listener of listeners) {
      listener(state);
    }
  };

  const stub = {
    async init(): Promise<void> {
      // no-op
    },
    getState(): AuthState {
      return state;
    },
    hasBackend(): boolean {
      return false;
    },
    subscribe(listener: (authState: AuthState) => void): () => void {
      listeners.push(listener);
      return () => {
        const index = listeners.indexOf(listener);
        if (index >= 0) {
          listeners.splice(index, 1);
        }
      };
    },
    async signInWithEmail(): Promise<{ error: string | null }> {
      return { error: "AUTH NOT CONFIGURED" };
    },
    async signUpWithEmail(): Promise<{ error: string | null }> {
      return { error: "AUTH NOT CONFIGURED" };
    },
    async signInWithGoogle(): Promise<void> {
      // no-op
    },
    async signInWithDiscord(): Promise<void> {
      // no-op
    },
    async signInWithEthereum(): Promise<{ error: string | null }> {
      return { error: "AUTH NOT CONFIGURED" };
    },
    async signInWithSolana(): Promise<{ error: string | null }> {
      return { error: "AUTH NOT CONFIGURED" };
    },
    async setUsername(username: string): Promise<{ error: string | null }> {
      const profile = state.profile;
      if (profile != null) {
        state = { ...state, profile: { ...profile, username } };
        notify();
      }
      return { error: null };
    },
    async addXp(_amount: number): Promise<void> {
      // no-op for guest
    },
    async refreshProfile() {
      return state.profile;
    },
    async loadAccountDebugSettings(): Promise<null> {
      return null;
    },
    async saveAccountDebugSettings(): Promise<boolean> {
      return false;
    },
    async signOut(): Promise<void> {
      state = { user: null, profile: null, loading: false };
      notify();
    }
  };

  return stub as unknown as AuthManager;
}

function samePerformanceSettings(a: PerformanceSettings, b: PerformanceSettings): boolean {
  return (
    a.preset === b.preset
    && a.sideFxMode === b.sideFxMode
    && a.sideFxDensityMode === b.sideFxDensityMode
    && a.backgroundStyleMode === b.backgroundStyleMode
    && a.backgroundBloomMode === b.backgroundBloomMode
    && a.backgroundGlowMode === b.backgroundGlowMode
    && a.backgroundSaturationMode === b.backgroundSaturationMode
    && a.backgroundMixMode === b.backgroundMixMode
    && a.backgroundMotionMode === b.backgroundMotionMode
    && a.particleMode === b.particleMode
    && a.opponentRenderMode === b.opponentRenderMode
    && a.fpsCap === b.fpsCap
  );
}

function sameDebugSettings(a: DebugSettings, b: DebugSettings): boolean {
  return (
    a.enabled === b.enabled
    && a.overlayGameplay === b.overlayGameplay
    && a.overlayBot === b.overlayBot
    && a.verbosity === b.verbosity
    && a.diagnostics === b.diagnostics
    && a.perfSafeMode === b.perfSafeMode
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseAdminDebugAllowlist(raw: string | undefined): string[] {
  if (raw == null) {
    return [];
  }
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

function syncViewportHeightVar(): void {
  const root = document.documentElement;
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  root.style.setProperty("--app-height", `${Math.round(viewportHeight)}px`);
}

function parseCssPxVar(style: CSSStyleDeclaration, name: string): number {
  const raw = style.getPropertyValue(name).trim();
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
}

function syncMenuSafeAreaInsets(
  renderer: CanvasRenderer,
  logicalDims: { width: number; height: number }
): void {
  const rootStyle = getComputedStyle(document.documentElement);
  const cssTopPx = parseCssPxVar(rootStyle, "--sat");
  const cssBottomPx = parseCssPxVar(rootStyle, "--sab");
  const canvasRect = renderer.canvas.getBoundingClientRect();
  const cssCanvasHeight = canvasRect.height > 0 ? canvasRect.height : logicalDims.height;
  const logicalPerCssPx = cssCanvasHeight > 0 ? logicalDims.height / cssCanvasHeight : 1;
  renderer.setMenuSafeAreaInsets(cssTopPx * logicalPerCssPx, cssBottomPx * logicalPerCssPx);
}

function registerServiceWorker(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  void navigator.serviceWorker
    .register("/sw.js")
    .then((registration) => {
      // Check for updates every 60 seconds so returning users get fresh code
      setInterval(() => {
        void registration.update();
      }, 60_000);
    })
    .catch((error) => {
      console.warn("[pwa] service worker registration failed", error);
    });
}

export async function runBootBeforeAuth(
  bootGate: () => Promise<BootStartResult>,
  runPostBootInit: () => Promise<void>
): Promise<BootStartResult> {
  const bootResult = await bootGate();
  if (bootResult !== "READY") {
    return bootResult;
  }
  await runPostBootInit();
  return bootResult;
}

export function applyVersusLocalViewOverlay(view: RenderModel, localView: RenderModel): void {
  view.run = localView.run;
  view.soloMode = localView.soloMode;
  view.soloModeLabel = localView.soloModeLabel;
  view.latestRunSoloMode = localView.latestRunSoloMode;
  view.latestRunSoloModeLabel = localView.latestRunSoloModeLabel;
  view.selectedSoloMode = localView.selectedSoloMode;
  view.soloCarouselIndex = localView.soloCarouselIndex;
  view.soloModeContinueAvailability = localView.soloModeContinueAvailability;
  view.soloModeLocks = localView.soloModeLocks;
  view.activeObjective = localView.activeObjective;
  view.activeObjectiveLabel = localView.activeObjectiveLabel;
  view.activeObjectiveReward = localView.activeObjectiveReward;
  view.goalLabel = localView.goalLabel;
  view.goalProgress = localView.goalProgress;
  view.goalTarget = localView.goalTarget;
  view.goalRemaining = localView.goalRemaining;
  view.goalRewardSummary = localView.goalRewardSummary;
  view.goalTargetRows = localView.goalTargetRows;
  view.goalTargetColors = localView.goalTargetColors;
  view.displayScore = localView.displayScore;
  view.levelTarget = localView.levelTarget;
  view.pointsToNextLevel = localView.pointsToNextLevel;
  view.warningActive = localView.warningActive;
  view.pendingRotation = localView.pendingRotation;
  view.rotationAnim = localView.rotationAnim;
  view.collapseAnim = localView.collapseAnim;
  view.swapAnim = localView.swapAnim;
  view.flashAlpha = localView.flashAlpha;
  view.flashColor = localView.flashColor;
  view.shakeMagnitude = localView.shakeMagnitude;
  view.hypeText = localView.hypeText;
  view.hypeTimer = localView.hypeTimer;
  view.lastScorePopup = localView.lastScorePopup;
  view.currentChainDepth = localView.currentChainDepth;
  view.doomClockSec = localView.doomClockSec;
  view.doomClockMaxSec = localView.doomClockMaxSec;
  view.doomClockFrozen = localView.doomClockFrozen;
  view.doomClockTimePopup = localView.doomClockTimePopup;
  view.cashPopupAmount = localView.cashPopupAmount;
  view.cashDenyFlashSec = localView.cashDenyFlashSec;
  view.armedPowerup = localView.armedPowerup;
  view.hintFirstPick = localView.hintFirstPick;
  view.powerupExecuting = localView.powerupExecuting;
  view.powerupExecutionRemainingSec = localView.powerupExecutionRemainingSec;
  view.dragState = localView.dragState;
  view.hoverCell = localView.hoverCell;
  view.hoveredPowerup = localView.hoveredPowerup;
  view.hintActive = localView.hintActive;
  view.hintCells = localView.hintCells;
  view.selectedSoloLeaderboardMode = localView.selectedSoloLeaderboardMode;
  view.musicEnabled = localView.musicEnabled;
  view.sfxEnabled = localView.sfxEnabled;
  view.hapticsEnabled = localView.hapticsEnabled;
  view.musicVolume = localView.musicVolume;
  view.sfxVolume = localView.sfxVolume;
  view.musicVisual = localView.musicVisual;
  view.debugViewModel = localView.debugViewModel;
  view.performance = localView.performance;
}

function applyCanvasDimensions(
  renderer: CanvasRenderer,
  input: InputManager
): { width: number; height: number } {
  const dims = computeCanvasDimensions();
  renderer.setCanvasSize(dims.width, dims.height);
  input.setLogicalSize(dims.width, dims.height);
  return dims;
}

async function bootstrap(): Promise<void> {
  syncViewportHeightVar();
  registerServiceWorker();

  const app = document.querySelector<HTMLDivElement>("#app");
  if (app == null) {
    throw new Error("Missing #app root");
  }

  const bootOverlay = new BootOverlayManager({
    host: app
  });
  bootOverlay.attach();
  const bootResult = await runBootBeforeAuth(
    () => bootOverlay.start(),
    async () => {
      bootOverlay.destroy();
    }
  );
  if (bootResult !== "READY") {
    return;
  }

  const assetManager = new AssetManager();
  const assetState = await assetManager.loadOptionalAtlas();

  const renderer = new CanvasRenderer(assetManager);
  renderer.attach(app);
  const input = new InputManager(renderer.canvas);

  // Apply platform-aware canvas dimensions on boot
  let currentDims = applyCanvasDimensions(renderer, input);
  syncMenuSafeAreaInsets(renderer, currentDims);

  const audio = new AudioManager();
  await hapticManager.init();
  adService.init();
  referralService.captureReferral();
  pwaInstallPrompt.init();

  networkManager.initWeb();

  const settings = loadSettings();
  audio.setEnabled(settings.musicEnabled);
  audio.setSfxEnabled(settings.sfxEnabled);
  audio.setMusicVolume(settings.musicVolume);
  audio.setSfxVolume(settings.sfxVolume);
  hapticManager.setEnabled(settings.hapticsEnabled);
  let lastMusicState = audio.isEnabled();
  let lastSfxState = audio.isSfxEnabled();
  let lastMusicVolume = audio.getMusicVolume();
  let lastSfxVolume = audio.getSfxVolume();
  let lastHapticsState = hapticManager.isEnabled();

  console.info("[rotatepanic] startup", {
    atlasMode: assetState.ready ? "atlas" : "fallback",
    missingRequiredFrames: assetState.missingRequiredFrameIds.length,
    atlasReason: assetState.reason,
    audioTrackMode: audio.getTrackMode()
  });

  let authManager: AuthManager;
  const authConfigured = hasSupabaseEnv();
  const multiplayerBackendAvailable = authConfigured && !FORCE_SKIP_AUTH;
  if (FORCE_SKIP_AUTH || !authConfigured) {
    if (!authConfigured) {
      console.warn(
        "[auth] Supabase env vars missing (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY). Using guest mode."
      );
    }
    authManager = createGuestAuthManager();
  } else {
    authManager = new AuthManager();
    try {
      await authManager.init();
    } catch (error) {
      console.error("[auth] init failed, falling back to guest mode", error);
      authManager = createGuestAuthManager();
    }
  }

  const bootEquipped = authManager.getState().profile?.equippedCharacter;
  if (bootEquipped != null) {
    primeCachedEquipped(bootEquipped);
  }

  const authOverlay = new AuthOverlay(authManager);
  authOverlay.attach(renderer.canvasWrapper);

  const postLoginDisclaimer = new PostLoginDisclaimerManager({
    host: app,
    redirectUrl: "https://akidcalledbeast.com"
  });
  postLoginDisclaimer.attach();

  // Username text input (overlaid on canvas for USERNAME_PICK phase)
  const usernameInput = document.createElement("input");
  usernameInput.type = "text";
  usernameInput.maxLength = 16;
  usernameInput.placeholder = "USERNAME";
  usernameInput.autocomplete = "off";
  usernameInput.autocapitalize = "off";
  usernameInput.spellcheck = false;
  Object.assign(usernameInput.style, {
    position: "absolute",
    left: "25%",
    top: "36.5%",
    width: "50%",
    height: "6%",
    boxSizing: "border-box",
    background: "rgba(15,22,40,0.95)",
    border: "1px solid #5c8cd4",
    borderRadius: "0px",
    color: "#d4e7ff",
    fontFamily: "'Press Start 2P', monospace",
    fontSize: "clamp(10px, 2.5vmin, 16px)",
    textAlign: "center",
    outline: "none",
    caretColor: "#ffd700",
    zIndex: "10",
    display: "none",
  });
  // Force lowercase and strip invalid chars as user types
  usernameInput.addEventListener("input", () => {
    usernameInput.value = usernameInput.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
  });
  renderer.canvasWrapper.appendChild(usernameInput);

  // Friend username input (overlaid on canvas for FRIENDS phase)
  const friendInput = document.createElement("input");
  friendInput.type = "text";
  friendInput.maxLength = 16;
  friendInput.placeholder = "ENTER USERNAME";
  friendInput.autocomplete = "off";
  friendInput.autocapitalize = "off";
  friendInput.spellcheck = false;
  Object.assign(friendInput.style, {
    position: "absolute",
    left: "11.1%",
    top: "22.7%",
    width: "59%",
    height: "4%",
    boxSizing: "border-box",
    background: "#0f1628",
    border: "none",
    borderRadius: "6px",
    color: "#d4e7ff",
    fontFamily: "'Press Start 2P', monospace",
    fontSize: "clamp(10px, 2.8vmin, 18px)",
    textAlign: "left",
    padding: "0 8px",
    outline: "none",
    caretColor: "#ffd700",
    zIndex: "10",
    display: "none",
  });
  friendInput.addEventListener("input", () => {
    friendInput.value = friendInput.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
  });
  renderer.canvasWrapper.appendChild(friendInput);

  const lobbyCodeInput = document.createElement("input");
  lobbyCodeInput.type = "text";
  lobbyCodeInput.maxLength = 6;
  lobbyCodeInput.placeholder = "LOBBY CODE";
  lobbyCodeInput.classList.add("lobby-chat-input");
  lobbyCodeInput.autocomplete = "off";
  lobbyCodeInput.autocapitalize = "off";
  lobbyCodeInput.spellcheck = false;
  Object.assign(lobbyCodeInput.style, {
    position: "absolute",
    left: "20%",
    top: "42%",
    width: "60%",
    height: "5%",
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    borderRadius: "8px",
    color: "#d4e7ff",
    fontFamily: "'Press Start 2P', monospace",
    fontSize: "clamp(8px, 2vmin, 13px)",
    textAlign: "left",
    textTransform: "uppercase",
    padding: "0 20px",
    outline: "none",
    caretColor: "#69e6ff",
    zIndex: "10",
    display: "none",
  });
  lobbyCodeInput.addEventListener("input", () => {
    lobbyCodeInput.value = lobbyCodeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  });
  renderer.canvasWrapper.appendChild(lobbyCodeInput);

  const lobbyChatInput = document.createElement("input");
  lobbyChatInput.type = "text";
  lobbyChatInput.maxLength = 120;
  lobbyChatInput.placeholder = "Type message...";
  lobbyChatInput.classList.add("lobby-chat-input");
  lobbyChatInput.autocomplete = "off";
  lobbyChatInput.autocapitalize = "off";
  lobbyChatInput.spellcheck = false;
  Object.assign(lobbyChatInput.style, {
    position: "absolute",
    left: "11%",
    top: "66%",
    width: "63%",
    height: "4%",
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    borderRadius: "8px",
    color: "#d4e7ff",
    fontFamily: "'Press Start 2P', monospace",
    fontSize: "clamp(8px, 2vmin, 13px)",
    textAlign: "left",
    padding: "0 20px",
    outline: "none",
    caretColor: "#69e6ff",
    zIndex: "10",
    display: "none",
  });
  renderer.canvasWrapper.appendChild(lobbyChatInput);

  const positionOverlayInput = (inputEl: HTMLInputElement, rect: Rect | undefined, inset = 0): void => {
    if (rect == null) {
      return;
    }
    const left = ((rect.x + inset) / currentDims.width) * 100;
    const top = ((rect.y + inset) / currentDims.height) * 100;
    const width = ((Math.max(1, rect.w - inset * 2)) / currentDims.width) * 100;
    const height = ((Math.max(1, rect.h - inset * 2)) / currentDims.height) * 100;
    inputEl.style.left = `${left}%`;
    inputEl.style.top = `${top}%`;
    inputEl.style.width = `${width}%`;
    inputEl.style.height = `${height}%`;
  };

  const refreshOpenLobbies = async (): Promise<void> => {
    if (!multiplayerBackendAvailable) {
      openLobbies = [];
      lobbyBrowserError = "MULTIPLAYER UNAVAILABLE";
      return;
    }
    try {
      const result = await lobbyManager.fetchOpenLobbies();
      openLobbies = result.lobbies;
      lobbyBrowserError = result.error ?? (result.lobbies.length === 0 ? "NO OPEN LOBBIES" : "");
      lobbyLastRefreshMs = Date.now();
    } catch {
      openLobbies = [];
      lobbyBrowserError = "FAILED TO FETCH LOBBIES";
    }
  };

  const refreshVersusLeaderboard = async (): Promise<void> => {
    const requestSeq = ++rankedLeaderboardRequestSeq;
    controller.setVersusLeaderboardLoading(true);
    try {
      if (!multiplayerBackendAvailable) {
        controller.setVersusLeaderboardData([], null);
        return;
      }
      const userId = authManager.getState().user?.id;
      const [entries, context] = await Promise.all([
        fetchRankedLeaderboard(100),
        userId != null && userId !== "guest-local"
          ? fetchRankedContext(userId, 1)
          : Promise.resolve(null)
      ]);
      if (requestSeq !== rankedLeaderboardRequestSeq) {
        return;
      }
      controller.setVersusLeaderboardData(entries, context);
    } catch {
      if (requestSeq !== rankedLeaderboardRequestSeq) {
        return;
      }
      controller.setVersusLeaderboardData([], null);
    } finally {
      if (requestSeq === rankedLeaderboardRequestSeq) {
        controller.setVersusLeaderboardLoading(false);
      }
    }
  };

  const sendLobbyChatFromInput = (): void => {
    if (controller.phase !== "VERSUS_LOBBY") {
      return;
    }
    const lobby = lobbyManager.getCurrentLobby();
    const profile = authManager.getState().profile;
    if (lobby == null || profile == null) {
      return;
    }
    const trimmed = lobbyChatInput.value.trim();
    if (!trimmed) {
      return;
    }
    const text = trimmed.slice(0, 120);
    lobbyRealtime.sendChat(profile.displayName, text);
    lobbyChatMessages.push({
      id: Date.now(),
      userId: profile.id,
      displayName: profile.displayName,
      message: text,
      createdAt: Date.now()
    });
    lobbyChatInput.value = "";
  };

  lobbyCodeInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleVersusAction("VERSUS_JOIN_LOBBY");
    }
  });

  lobbyChatInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      sendLobbyChatFromInput();
    }
  });

  // Friends error display state
  let friendsError = "";
  let friendsErrorTimer = 0;
  let friendsPollTimer = 0;
  let friendsPage = 0;
  let friendRemovePromptCursor = 0;
  let friendRemovePrompt: string = FRIEND_REMOVE_PROMPTS[0];
  let pendingFriendRemoval: { odId: string; username: string } | null = null;
  let friendRemoveBusy = false;
  let openLobbies: LobbyData[] = [];
  let lobbyBrowserError = "";
  let lobbyLastRefreshMs = 0;
  let lobbyJoinInputOpen = false;
  let rankedLeaderboardRequestSeq = 0;
  let lobbyBrowsePollTimer = 0;
  const FRIENDS_POLL_ACTIVE = 10;   // seconds between polls while on FRIENDS page
  const FRIENDS_POLL_BG = 30;       // seconds between background polls (for online count)
  let usernamePickReturnPhase: "TITLE" | "PROFILE_SETTINGS" = "TITLE";

  const controller = new GameController(audio, authManager);
  controller.setPerformanceSettings(settings.performance);
  let lastPerformanceSettings = controller.getPerformanceSettings();
  const adminDebugAllowlist = parseAdminDebugAllowlist(import.meta.env.VITE_ADMIN_DEBUG_ALLOWLIST as string | undefined);
  const resolveDebugAccessForAuthState = (authState: AuthState): AdminDebugAccess =>
    resolveAdminDebugAccess({
      user: authState.user,
      profile: authState.profile,
      authBackendAvailable: authConfigured && !FORCE_SKIP_AUTH,
      allowlist: adminDebugAllowlist
    });
  let currentDebugAccess = resolveDebugAccessForAuthState(authManager.getState());
  controller.setDebugAccess(currentDebugAccess);
  const initialDebugLoad = await loadDebugSettings({
    userId: authManager.getState().user?.id ?? null,
    canUseDebugSettings: currentDebugAccess.canUseDebugSettings,
    loadFromAccount: authManager.hasBackend()
      ? (userId: string) => authManager.loadAccountDebugSettings(userId)
      : undefined
  });
  controller.setDebugSettings(initialDebugLoad.settings, "bootstrap_load");
  let lastDebugSettings = controller.getDebugSettings();
  let lastDebugActorId = authManager.getState().user?.id ?? null;
  let debugSyncVersion = 0;
  let versusCtrl: VersusController | null = null;
  let versusOptionsOpen = false;

  const activeControllerForImmediateHaptics = (): GameController => (
    versusCtrl != null && controller.phase === "VERSUS_MATCH"
      ? versusCtrl.localController
      : controller
  );
  input.setImmediateGestureCallbacks({
    onBoardClickQueued: (click) => {
      activeControllerForImmediateHaptics().handleGestureBoardClickImmediate(click);
    },
    onSwapQueued: (move) => {
      activeControllerForImmediateHaptics().handleGestureSwapImmediate(move);
    },
    onShareRequested: () => {
      const username = authManager.getState().profile?.username;
      if (username) {
        void referralService.shareReferralLink(username).catch(() => {
          // Fallback: copy link if share sheet fails
          copyToClipboard(referralService.buildReferralLink(username));
          friendsError = "LINK COPIED!";
          friendsErrorTimer = 3;
        });
      }
    },
    onCopyRequested: () => {
      const username = authManager.getState().profile?.username;
      const link = username
        ? referralService.buildReferralLink(username)
        : "https://bitbeast.gg";
      copyToClipboard(link);
      friendsError = "LINK COPIED!";
      friendsErrorTimer = 3;
    },
  });

  const applyDebugAccessToControllers = (access: AdminDebugAccess): void => {
    controller.setDebugAccess(access);
    if (versusCtrl != null) {
      versusCtrl.localController.setDebugAccess(access);
      versusCtrl.mirrorController.setDebugAccess(access);
    }
  };

  const applyDebugSettingsToControllers = (debugSettings: DebugSettings, source: string): void => {
    controller.setDebugSettings(debugSettings, source);
    if (versusCtrl != null) {
      versusCtrl.localController.setDebugSettings(debugSettings, source);
      versusCtrl.mirrorController.setDebugSettings(debugSettings, source);
    }
  };

  const syncDebugForAuthState = async (authState: AuthState, source: string): Promise<void> => {
    const syncId = ++debugSyncVersion;
    const previousAccess = currentDebugAccess;
    const nextAccess = resolveDebugAccessForAuthState(authState);
    currentDebugAccess = nextAccess;
    applyDebugAccessToControllers(nextAccess);
    const actorId = authState.user?.id ?? null;
    const shouldReload =
      actorId !== lastDebugActorId
      || previousAccess.canUseDebugSettings !== nextAccess.canUseDebugSettings;
    if (!shouldReload) {
      return;
    }
    lastDebugActorId = actorId;
    const loaded = await loadDebugSettings({
      userId: actorId,
      canUseDebugSettings: nextAccess.canUseDebugSettings,
      loadFromAccount: authManager.hasBackend()
        ? (userId: string) => authManager.loadAccountDebugSettings(userId)
        : undefined
    });
    if (syncId !== debugSyncVersion) {
      return;
    }
    applyDebugSettingsToControllers(loaded.settings, source);
    lastDebugSettings = loaded.settings;
  };

  controller.onDebugModeTransition = ({ enabled, source }) => {
    const actorId = authManager.getState().user?.id ?? "guest-local";
    console.info("[audit] debug_mode_transition", {
      actorId,
      action: enabled ? "enable" : "disable",
      timestamp: new Date().toISOString(),
      source
    });
  };

  const lobbyManager = new LobbyManager();
  const lobbyRealtime = new LobbyRealtime();
  const friendManager = new FriendManager();
  const messageService = new MessageService();

  // Wire friend manager to auth state
  const isAppearOffline = (): boolean => {
    try { return localStorage.getItem("bitbeast.appearOffline") === "1"; } catch { return false; }
  };
  const initFriendManager = (userId: string, userCreatedAt?: string): void => {
    if (userId !== "guest-local" && authConfigured) {
      void friendManager.init(userId, isAppearOffline());
      messageService.init(userId, userCreatedAt);
    }
  };
  const initialUser = authManager.getState().user;
  if (initialUser != null) {
    initFriendManager(initialUser.id, initialUser.created_at);
  }
  authManager.subscribe((authState) => {
    postLoginDisclaimer.maybeShowAfterLogin(authState);
    if (authState.user != null) {
      initFriendManager(authState.user.id, authState.user.created_at);
    } else {
      friendManager.destroy();
      messageService.destroy();
    }
    // Clear auth loading spinner when auth state settles
    if (!authState.loading) {
      if (currentLoadingAction?.startsWith("auth_")) {
        currentLoadingAction = null;
      }
    }
    void syncDebugForAuthState(authState, "auth_state_change");
  });

  // Fetch player rank context early so vinyl tier shows immediately
  const refreshPlayerRankContext = async (): Promise<void> => {
    if (!multiplayerBackendAvailable) return;
    const userId = authManager.getState().user?.id;
    if (userId == null || userId === "guest-local") return;
    try {
      const context = await fetchRankedContext(userId, 1);
      controller.setVersusRankContext(context);
    } catch { /* ignore */ }
  };

  // Keep lobby manager in sync with auth state
  const syncLobbyPlayer = (authState: AuthState): void => {
    const p = authState.profile;
    if (p != null) {
      lobbyManager.setLocalPlayer(p.id, p.displayName, p.versusElo, p.level);
      void refreshPlayerRankContext();
    }
    if (
      (controller.phase === "LEADERBOARD" || controller.isPhoneHighscoresVisible())
      && controller.getLeaderboardTab() === "versus"
    ) {
      void refreshVersusLeaderboard();
    }
  };
  syncLobbyPlayer(authManager.getState());
  authManager.subscribe(syncLobbyPlayer);

  // Load wallet & inventory for current user
  const syncEquippedCharacter = (): void => {
    const char = getCachedEquippedCharacter();
    controller.setEquippedCharacter({ icon: char.icon, color: char.color, name: char.name, imagePath: char.imagePath, imageSmallPath: char.imageSmallPath });
  };
  const readEquippedIdForProfileSelection = (): string | null => {
    try {
      return localStorage.getItem("bitbeast.equipped.v1") ?? null;
    } catch {
      return null;
    }
  };
  const getOwnedSkinsForProfileSelection = () => {
    const seen = new Set<string>();
    return getCachedOwnedIds()
      .map((id) => getCharacterById(id))
      .filter((skin): skin is CharacterDef => {
        if (skin == null || seen.has(skin.id)) return false;
        seen.add(skin.id);
        return true;
      });
  };
  const getNftAvatarsForProfileSelection = () => {
    const walletState = walletService.getState();
    const delegatedTokenMap = walletState.delegatedTokenMap;
    const delegatedTokenIds = Object.keys(delegatedTokenMap).length > 0
      ? new Map(Object.entries(delegatedTokenMap))
      : null;
    return resolveAvatarList(
      walletState.unlockedTokenIds,
      readEquippedIdForProfileSelection(),
      delegatedTokenIds,
    );
  };
  const getSelectedProfileItem = (slotIndex: number) => {
    const vm = controller.getViewModel();
    return getSelectedProfileVisibleItem(
      vm.profileFilter,
      getOwnedSkinsForProfileSelection(),
      getNftAvatarsForProfileSelection(),
      readEquippedIdForProfileSelection(),
      vm.profileViewMode,
      vm.profilePage,
      vm.profileBrowseIndex,
      slotIndex,
    );
  };
  syncEquippedCharacter();

  // Onboarding persistence uses save.ts: hasOnboardingBeenSeen / markOnboardingSeen / resetOnboardingSeen

  let dailyLoginStatus: DailyLoginBonusStatus | null = getCachedDailyLoginBonusStatus();
  let dailyLoginLoading = false;
  let dailyLoginClaimPending = false;
  let lastObservedDailyLoginUtc = currentUtcDateString();

  const refreshDailyLoginStatus = async (
    userId: string,
    options: { autoOpen?: boolean } = {}
  ): Promise<DailyLoginBonusStatus | null> => {
    if (userId === "guest-local") {
      dailyLoginStatus = null;
      dailyLoginLoading = false;
      controller.setDailyLoginModalOpen(false);
      return null;
    }

    dailyLoginLoading = true;
    try {
      const status = await fetchDailyLoginBonusStatus(userId);
      dailyLoginStatus = status;
      if (options.autoOpen && shouldAutoOpenDailyLoginBonus(userId, status)) {
        controller.setDailyLoginModalOpen(true);
      }
      return status;
    } finally {
      dailyLoginLoading = false;
    }
  };

  /** Clear all user-specific caches on sign-out so a new account starts fresh. */
  const clearUserCaches = (): void => {
    resetWalletCache();
    resetInventoryCache();
    try {
      resetOnboardingSeen();
      localStorage.removeItem("bitbeast.username");
    } catch { /* ignore */ }
    controller.setWalletBalance(0, 0);
    controller.setDailyLoginModalOpen(false);
    dailyLoginStatus = null;
    dailyLoginLoading = false;
    dailyLoginClaimPending = false;
    lastObservedDailyLoginUtc = currentUtcDateString();
    syncEquippedCharacter();
  };

  const loadEconomy = async (userId: string): Promise<void> => {
    if (userId !== "guest-local") {
      const [balance, owned, equipped] = await Promise.all([
        fetchBalance(userId),
        fetchInventory(userId),
        fetchEquipped(userId)
      ]);
      controller.setWalletBalance(balance.coins, balance.gems);
      void refreshDailyLoginStatus(userId, { autoOpen: true });
      if (!equipped) {
        const assigned = owned[0];
        if (assigned != null) {
          equipCharacterLocal(assigned);
        }
      }
    } else {
      const balance = getCachedBalance();
      controller.setWalletBalance(balance.coins, balance.gems);
      controller.setDailyLoginModalOpen(false);
      dailyLoginStatus = null;
      dailyLoginLoading = false;
      dailyLoginClaimPending = false;
    }
    syncEquippedCharacter();

    // Prevent onboarding flash during async page-load hydration.
    // New users still enter onboarding after explicit username claim flow.
  };

  // Referral stats: cached, refreshed on auth change
  let cachedReferralStats: { count: number; max: number } | null = null;
  let cachedPushEnabled = false;
  let cachedPushSupported = false;
  void pushService.isSupported().then((s) => { cachedPushSupported = s; }).catch((err) => console.warn("[push] isSupported failed:", err instanceof Error ? err.message : String(err)));
  const refreshPushState = (): void => {
    void pushService.isSubscribed().then((sub) => { cachedPushEnabled = sub; }).catch((err) => console.warn("[push] isSubscribed failed:", err instanceof Error ? err.message : String(err)));
  };
  refreshPushState();
  const refreshReferralStats = (): void => {
    const userId = authManager.getState().user?.id;
    if (userId != null && userId !== "guest-local") {
      void referralService.getReferralStats(userId).then((stats) => {
        cachedReferralStats = stats;
      }).catch((err) => console.warn("[referral] stats fetch failed:", err instanceof Error ? err.message : String(err)));
    }
  };

  const initialUserId = authManager.getState().user?.id;
  if (initialUserId != null) {
    void loadEconomy(initialUserId);
  }
  void staminaService.init(initialUserId ?? null);
  if (initialUserId != null) {
    refreshReferralStats();
  }

  // Track previous user ID to detect guest-to-authenticated transitions (#145)
  let previousUserId: string | null = initialUserId ?? null;

  /** Snapshot guest local progress and migrate it to the authenticated profile. */
  const migrateGuestProgress = async (newUserId: string): Promise<void> => {
    // Snapshot local balance before loadEconomy overwrites cache
    const guestBalance = getCachedBalance();

    // Snapshot local run states for all solo modes
    const guestRuns: Array<{ mode: typeof SOLO_MODES[number]; run: import("./game/types").RunState }> = [];
    for (const mode of SOLO_MODES) {
      const run = loadRunState(mode);
      if (run != null) {
        guestRuns.push({ mode, run });
      }
    }

    // Snapshot local XP
    let guestXp = 0;
    try {
      guestXp = Math.max(0, Number(localStorage.getItem("bitbeast.totalXp")) || 0);
    } catch { /* ignore */ }

    const hasProgress = guestBalance.coins > 0 || guestBalance.gems > 0 || guestRuns.length > 0 || guestXp > 0;
    if (!hasProgress) return;

    console.info("[auth] migrating guest progress →", newUserId, {
      coins: guestBalance.coins,
      gems: guestBalance.gems,
      runs: guestRuns.length,
      xp: guestXp
    });

    // Credit guest coins/gems to the new profile
    if (guestBalance.coins > 0) {
      await creditCoins(newUserId, guestBalance.coins, "guest_migration").catch(() => null);
    }
    if (guestBalance.gems > 0) {
      await creditGems(newUserId, guestBalance.gems, "guest_migration").catch(() => null);
    }

    // Upload guest run states to cloud saves
    for (const { mode, run } of guestRuns) {
      await saveRunStateCloud(newUserId, run, mode).catch(() => false);
    }

    // Migrate XP
    if (guestXp > 0) {
      await authManager.addXp(guestXp).catch(() => null);
    }
  };

  authManager.subscribe((authState) => {
    const newUserId = authState.user?.id ?? null;
    const wasGuest = previousUserId == null || previousUserId === "guest-local";
    const isAuthenticated = newUserId != null && newUserId !== "guest-local";
    previousUserId = newUserId;

    if (authState.user != null) {
      lastObservedDailyLoginUtc = currentUtcDateString();
      primeCachedEquipped(authState.profile?.equippedCharacter);
      syncEquippedCharacter();

      // If transitioning from guest to authenticated, migrate local progress first
      if (wasGuest && isAuthenticated) {
        const userId = authState.user.id;
        void migrateGuestProgress(userId).finally(() => {
          void loadEconomy(userId);
        });
      } else {
        void loadEconomy(authState.user.id);
      }
      void staminaService.init(authState.user.id);
      refreshReferralStats();
    } else {
      // User signed out — clear all cached data so the next account starts fresh
      clearUserCaches();
      void staminaService.init(null);
      cachedReferralStats = null;
    }
  });

  // Token shop state: cached whitelisted token existence check (re-loaded on auth change)
  let whitelistedTokensExist = false;

  // Load token shop items + whitelisted tokens on startup, precompute prices immediately
  void (async () => {
    await tokenShopService.loadItems();
    const tokens = await tokenShopService.loadWhitelistedTokens();
    whitelistedTokensExist = tokens.length > 0;
    if (tokens.length > 0) {
      await tokenShopService.precomputePrices(tokens);
    }
  })();
  // Reload token shop on auth change (items/tokens may be personalized in future)
  authManager.subscribe((authState) => {
    if (authState.user != null) {
      void (async () => {
        await tokenShopService.loadItems();
        const tokens = await tokenShopService.loadWhitelistedTokens();
        whitelistedTokensExist = tokens.length > 0;
        if (tokens.length > 0) {
          await tokenShopService.precomputePrices(tokens);
        }
      })();
    }
  });

  // Wire onboarding confirm callback
  controller.onOnboardingConfirm = (skinId) => {
    const char = getCharacterById(skinId);
    if (char == null) return;
    const userId = authManager.getState().user?.id;
    const isGuest = userId == null || userId === "guest-local";

    if (isGuest) {
      addOwnedLocal(skinId);
      equipCharacterLocal(skinId);
      syncEquippedCharacter();
    } else {
      // Add to owned + equip
      addOwnedLocal(skinId);
      void equipCharacter(userId, skinId).then(() => {
        syncEquippedCharacter();
      }).catch((err) => console.warn("[equip] character equip failed:", err instanceof Error ? err.message : String(err)));
    }
    markOnboardingSeen();
  };

  // Wire onboarding tour complete — auto-start practice match vs Rafsby
  controller.onOnboardingTourComplete = () => {
    startOnboardingBotMatch();
  };

  // Wire ad gate callbacks
  controller.onAdGateWatchAd = () => {
    input.setModalOpen(true);
    void adService.showAd().then(() => {
      input.setModalOpen(false);
      void controller.restoreStaminaFromAd();
    }).catch((err) => {
      input.setModalOpen(false);
      console.warn("[ad] showAd failed:", err instanceof Error ? err.message : String(err));
    });
  };
  // Quick ad: no hearts → ad pops up → hearts refill
  controller.onQuickAdForStamina = () => {
    if (controller.phase === "VERSUS_RESULT") {
      // Versus flow: show ad in background, restore heart immediately
      void adService.showAd();
      staminaService.restoreFromAdOptimistic();
      controller.resetVersusResultStepTimer();
    } else {
      void adService.showAd().then(() => {
        void controller.restoreStaminaFromAd();
      }).catch((err) => {
        console.warn("[ad] showAd failed:", err instanceof Error ? err.message : String(err));
        controller.fallbackPendingStaminaIntentToGate();
      });
    }
  };
  controller.onAdGateConnectWallet = () => {
    void (async () => {
      try {
        await ensureAppKit();
        const { openWalletModal } = await import("./wallet/appKitService");
        openWalletModal();
      } catch (err) {
        console.warn("[ad-gate] wallet connect error:", err);
      }
    })();
  };

  // Wire referral share callback
  controller.onShareReferral = () => {
    const username = authManager.getState().profile?.username;
    if (username != null) {
      void referralService.shareReferralLink(username);
    }
  };

  // Wire PWA install callback
  controller.onInstallApp = () => {
    void pwaInstallPrompt.prompt();
    pwaInstallPrompt.markShown();
  };

  // Wire push notification toggle
  controller.onTogglePushNotifications = () => {
    const userId = authManager.getState().user?.id;
    if (userId == null || userId === "guest-local") return;
    if (cachedPushEnabled) {
      void pushService.unsubscribe(userId).then(() => {
        cachedPushEnabled = false;
      }).catch((err) => console.warn("[push] unsubscribe failed:", err instanceof Error ? err.message : String(err)));
    } else {
      void pushService.subscribe(userId).then((ok) => {
        cachedPushEnabled = ok;
      }).catch((err) => console.warn("[push] subscribe failed:", err instanceof Error ? err.message : String(err)));
    }
  };

  // Wire versus action callback
  controller.onVersusAction = handleVersusAction;

  // Wire shop action callback
  controller.onShopAction = (action) => {
    const userId = authManager.getState().user?.id;
    const isGuest = userId == null || userId === "guest-local";
    const shopSkins = controller.getViewModel().shopSkins;

    if (action === "SHOP_BUY") {
      const idx = input.consumeShopBuyIndex();
      if (idx == null) return;
      if (idx >= shopSkins.length) return;
      const char = shopSkins[idx];

      // Guard: can't buy if already owned
      if (getCachedOwnedIds().includes(char.id)) return;
      // Guard: insufficient gems
      if (getCachedBalance().gems < char.priceGems) {
        controller.showMessage("NOT ENOUGH GEMS");
        return;
      }

      if (isGuest) {
        const bal = deductGemsLocal(char.priceGems);
        if (bal == null) {
          controller.showMessage("NOT ENOUGH GEMS");
          return;
        }
        addOwnedLocal(char.id);
        controller.setWalletBalance(bal.coins, bal.gems);
      } else {
        void purchaseCharacter(userId, char.id).then((result) => {
          if (result.ok) {
            // Use gemsRemaining from purchase result if available, else fetch
            if (result.gemsRemaining != null) {
              const bal = getCachedBalance();
              controller.setWalletBalance(bal.coins, result.gemsRemaining);
            } else {
              void fetchBalance(userId).then((bal) => {
                controller.setWalletBalance(bal.coins, bal.gems);
              }).catch((err) => console.warn("[shop] balance refresh failed:", err instanceof Error ? err.message : String(err)));
            }
            void fetchInventory(userId);
          } else {
            console.warn("[shop] purchase failed:", result.error);
            controller.showMessage(
              result.error === "Insufficient gems" ? "NOT ENOUGH GEMS"
              : result.error === "Already owned" ? "ALREADY OWNED"
              : "PURCHASE FAILED"
            );
          }
        }).catch((err) => console.warn("[shop] purchase failed:", err instanceof Error ? err.message : String(err)));
      }
      return;
    }

    if (action === "SHOP_EQUIP") {
      const idx = input.consumeShopEquipIndex();
      if (idx == null) return;
      if (idx >= shopSkins.length) return;
      const char = shopSkins[idx];

      // Optimistic local update for instant UI feedback
      equipCharacterLocal(char.id);
      syncEquippedCharacter();
      // Persist to Supabase (if logged in)
      if (!isGuest) {
        void equipCharacter(userId, char.id);
      }
      return;
    }

    if (action === "SHOP_EXCHANGE") {
      if (isGuest) {
        console.warn("[shop] exchange as guest (local only)");
        const result = exchangeCoinsToGemsLocal(100);
        if (result != null) {
          controller.setWalletBalance(result.coins, result.gems);
          void audio.playSfx("buy_gems");
        } else {
          controller.showMessage("NOT ENOUGH COINS");
        }
      } else {
        void exchangeCoinsToGems(userId, 100).then((result) => {
          if (result != null) {
            controller.setWalletBalance(result.coins, result.gems);
            void audio.playSfx("buy_gems");
          } else {
            controller.showMessage("NOT ENOUGH COINS");
            console.warn("[shop] exchange RPC returned null -- check console for errors");
          }
        }).catch((err) => console.warn("[shop] coin exchange failed:", err instanceof Error ? err.message : String(err)));
      }
      return;
    }

    if (action === "SHOP_BUY_GEMS") {
      const idx = input.consumeShopBuyGemsIndex();
      if (idx == null || idx >= GEM_PACKS.length) return;
      const pack = GEM_PACKS[idx];

      if (isGuest) {
        const result = buyGemPackLocal(pack.coinCost, pack.gems);
        if (result != null) {
          controller.setWalletBalance(result.coins, result.gems);
          controller.triggerGemBurst(idx);
          void audio.playSfx("buy_gems");
        } else {
          controller.showMessage("NOT ENOUGH COINS");
        }
      } else {
        void buyGemPack(userId, pack.coinCost, pack.gems).then((result) => {
          if (result != null) {
            controller.setWalletBalance(result.coins, result.gems);
            controller.triggerGemBurst(idx);
            void audio.playSfx("buy_gems");
          } else {
            controller.showMessage("NOT ENOUGH COINS");
          }
        }).catch((err) => console.warn("[shop] gem pack purchase failed:", err instanceof Error ? err.message : String(err)));
      }
      return;
    }

    if (action === "SHOP_BUY_SOL") {
      const idx = input.consumeShopBuyGemsIndex();
      if (idx == null || idx >= GEM_PACKS.length) return;
      const pack = GEM_PACKS[idx];
      // Find matching token shop item by gem amount
      const shopState = tokenShopService.getState();
      const solItem = shopState.items.find((t) => t.gemAmount === pack.gems);
      if (!solItem) {
        controller.showMessage("SOL PRICE UNAVAILABLE");
        return;
      }
      void (async () => {
        try {
          await ensureAppKit();
          const { getSolProvider, openWalletModal, isConnected } = await import("./wallet/appKitService");
          // If wallet not connected, open modal to connect first
          if (!isConnected()) {
            openWalletModal("solana");
            return;
          }
          const provider = getSolProvider();
          if (provider == null) {
            openWalletModal("solana");
            return;
          }
          // Wallet connected — select item and start purchase flow
          const tokens = await tokenShopService.loadWhitelistedTokens();
          if (tokens.length === 0) {
            controller.showMessage("NO TOKENS AVAILABLE");
            return;
          }
          tokenShopService.selectItem(solItem, tokens[0]);
        } catch (err) {
          console.warn("[gemShop] SHOP_BUY_SOL error:", err);
        }
      })();
      return;
    }

    if (action === "SHOP_BUY_IAP") {
      if (!iapManager.isNativeIapAvailable()) return;
      const idx = input.consumeShopBuyGemsIndex();
      if (idx == null) return;

      // If already in a purchase flow, ignore
      if (iapPurchaseState.phase !== "idle") return;

      const packs = iapManager.getAvailablePackages();
      if (idx >= packs.length) return;

      const pkg = packs[idx];
      const def = getIapPackByProductId(pkg.product.identifier);
      if (!def) return;

      // Show confirmation dialog
      iapPurchaseState = {
        phase: "confirm",
        packIndex: idx,
        gems: def.gems,
        priceString: pkg.product.priceString,
        label: def.label,
        message: "",
        messageTimer: 0,
      };
      return;
    }

    if (action === "SHOP_IAP_CONFIRM") {
      if (iapPurchaseState.phase !== "confirm") return;

      const idx = iapPurchaseState.packIndex;
      const packs = iapManager.getAvailablePackages();
      if (idx < 0 || idx >= packs.length) {
        iapPurchaseState = { ...iapPurchaseState, phase: "idle" };
        return;
      }

      const pkg = packs[idx];
      iapPurchaseState = { ...iapPurchaseState, phase: "loading" };

      void (async () => {
        try {
          const result = await iapManager.purchase(pkg.product.identifier);
          if (result.success) {
            // Credit gems
            if (isGuest) {
              const bal = creditGemsLocal(result.gems);
              controller.setWalletBalance(bal.coins, bal.gems);
            } else {
              const bal = await creditGems(userId!, result.gems, "iap_purchase");
              if (bal) controller.setWalletBalance(bal.coins, bal.gems);
            }
            controller.triggerGemBurst();
            void audio.playSfx("coin_gain", { pitchMultiplier: 1.2 });
            console.info("[iap] Purchase complete:", result.gems, "gems credited");
            iapPurchaseState = {
              ...iapPurchaseState,
              phase: "success",
              message: `+${result.gems} Gems!`,
              messageTimer: 2.5,
            };
          } else {
            const errMsg = result.error === "Purchase cancelled" ? "Purchase cancelled" : `Purchase failed: ${result.error}`;
            iapPurchaseState = {
              ...iapPurchaseState,
              phase: "error",
              message: errMsg,
              messageTimer: 3.0,
            };
          }
        } catch (err) {
          console.warn("[iap] Purchase failed:", err);
          iapPurchaseState = {
            ...iapPurchaseState,
            phase: "error",
            message: "Purchase failed. Please try again.",
            messageTimer: 3.0,
          };
        }
      })();
      return;
    }

    if (action === "SHOP_IAP_CANCEL") {
      // Reset to idle from any state (confirm, success, error)
      iapPurchaseState = {
        phase: "idle", packIndex: -1, gems: 0, priceString: "", label: "", message: "", messageTimer: 0,
      };
      return;
    }

    if (action === "SHOP_RESTORE_IAP") {
      if (!iapManager.isNativeIapAvailable()) return;
      void (async () => {
        try {
          const result = await iapManager.restore();
          console.info("[iap] Restore complete:", result.restoredTransactions, "transactions found");
          // For consumables, restore confirms history but does not re-deliver gems
          // (Apple consumable semantics -- gems were delivered at purchase time)
        } catch (err) {
          console.warn("[iap] Restore failed:", err);
        }
      })();
    }

    if (action === "TOKEN_SHOP_SELECT") {
      const idx = input.consumeTokenShopItemIndex();
      if (idx == null) return;
      const shopState = tokenShopService.getState();
      const items = shopState.items;
      if (idx >= items.length) return;
      void (async () => {
        try {
          const tokens = await tokenShopService.loadWhitelistedTokens();
          if (tokens.length === 0) {
            console.warn("[tokenShop] No whitelisted tokens available");
            return;
          }
          // Use first active whitelisted token (multi-token UI deferred for now)
          tokenShopService.selectItem(items[idx], tokens[0]);
        } catch (err) {
          console.warn("[tokenShop] TOKEN_SHOP_SELECT error:", err);
        }
      })();
      return;
    }

    if (action === "TOKEN_SHOP_CONFIRM") {
      void (async () => {
        // Ensure AppKit is initialized and a live Solana provider exists
        await ensureAppKit();
        try {
          const { getSolProvider, openWalletModal } = await import("./wallet/appKitService");
          const provider = getSolProvider();
          if (provider == null) {
            // No live provider â€" open wallet modal so user can reconnect
            if (import.meta.env.DEV) console.log("[tokenShop] No live Solana provider, opening wallet modal");
            tokenShopService.cancelPurchase();
            openWalletModal("solana");
            return;
          }
        } catch {
          // AppKit not available â€" cancel and prompt connect
          tokenShopService.cancelPurchase();
          return;
        }

        // Check sufficient SOL balance before sending to chain
        const shopState = tokenShopService.getState();
        if (shopState.tokenPrice != null) {
          const solBal = solanaService.getState().tokenBalances.find(b => b.symbol === "SOL");
          if (solBal && solBal.balance < shopState.tokenPrice) {
            tokenShopService.setError(`Insufficient SOL â€" need ${shopState.tokenPrice.toFixed(4)} but have ${solBal.balance.toFixed(4)}`);
            return;
          }
        }

        let verifyInterval: ReturnType<typeof setInterval> | null = null;
        verifyInterval = setInterval(() => {
          const s = tokenShopService.getState();
          if (s.purchaseState !== "verifying") {
            if (verifyInterval) { clearInterval(verifyInterval); verifyInterval = null; }
            return;
          }
          tokenShopService.incrementVerifyAttempt();
        }, 3000);

        const result = await tokenShopService.confirmPurchase();
        if (verifyInterval) { clearInterval(verifyInterval); verifyInterval = null; }
        if (result.success) {
          // Refresh Solana token balances after successful purchase
          void solanaService.fetchBalances();
          // Refresh gem balance if gems were credited
          const userId = authManager.getState().user?.id;
          if (userId != null && userId !== "guest-local") {
            void fetchBalance(userId).then((bal) => {
              controller.setWalletBalance(bal.coins, bal.gems);
            }).catch((err) => console.warn("[token-shop] balance refresh failed:", err instanceof Error ? err.message : String(err)));
          }
          controller.triggerGemBurst();
          void audio.playSfx("coin_gain", { pitchMultiplier: 1.2 });
        }
      })();
      return;
    }

    if (action === "TOKEN_SHOP_RECHECK") {
      void (async () => {
        const shopState = tokenShopService.getState();
        if (shopState.purchaseState !== "verifying") return;
        const txSignature = shopState.lastTxSignature;
        const purchaseId = shopState.lastPurchaseId;
        if (!txSignature) {
          tokenShopService.setError("No transaction signature available to recheck");
          return;
        }
        const supabase = (await import("./auth/supabaseClient")).getSupabase();
        const { data, error } = await supabase.functions.invoke("token-purchase-verify", {
          body: {
            txSignature,
            purchaseId,
          },
        });
        if (error) {
          tokenShopService.setError(error.message ?? "Recheck failed");
          return;
        }
        const result = data as { status: string; purchaseId?: string; error?: string };
        if (result.status === "verified") {
          tokenShopService.setSuccess(result.purchaseId ?? null);
          void solanaService.fetchBalances();
          const userId = authManager.getState().user?.id;
          if (userId != null && userId !== "guest-local") {
            void fetchBalance(userId).then((bal) => {
              controller.setWalletBalance(bal.coins, bal.gems);
            }).catch((err) => console.warn("[token-shop] balance refresh failed:", err instanceof Error ? err.message : String(err)));
          }
          controller.triggerGemBurst();
          void audio.playSfx("coin_gain", { pitchMultiplier: 1.2 });
        }
      })();
      return;
    }

    if (action === "TOKEN_SHOP_CANCEL" || action === "TOKEN_SHOP_DISMISS") {
      tokenShopService.cancelPurchase();
      return;
    }
  };

  // Wire profile action callback (equip character from profile page)
  controller.onProfileAction = (action) => {
    if (action !== "PROFILE_EQUIP") return;
    const slotIndex = input.consumeProfileEquipIndex();
    if (slotIndex == null) return;
    const selectedItem = getSelectedProfileItem(slotIndex);
    if (selectedItem == null || selectedItem.kind !== "skin") return;
    const skinId = selectedItem.targetId;
    const userId = authManager.getState().user?.id;
    const isGuest = userId == null || userId === "guest-local";

    // Optimistic local update for instant UI feedback
    equipCharacterLocal(skinId);
    syncEquippedCharacter();
    // Persist to Supabase (if logged in)
    if (!isGuest) {
      void equipCharacter(userId, skinId);
    }
  };

  // â"€â"€ ETH Wallet / NFT Avatar wiring â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  let walletConnecting = false;
  let nftSyncing = false;
  // â"€â"€ Solana Wallet wiring â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  let solConnecting = false;

  // â"€â"€ AppKit lazy init â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  let appKitReady = false;
  async function ensureAppKit(): Promise<void> {
    if (appKitReady) return;
    const { initAppKit, subscribeToProviders, subscribeToState } = await import("./wallet/appKitService");
    const modal = initAppKit();
    if (modal == null) return; // VITE_REOWN_PROJECT_ID not set â€" game continues without wallets

    subscribeToProviders((state: Record<string, unknown>) => {
      const eip155Provider = state["eip155"];
      const solProvider = state["solana"];

      if (eip155Provider && !walletService.getState().address) {
        void walletService.connectFromProvider(eip155Provider);
      }
      if (!eip155Provider && walletService.getState().address) {
        walletService.onDisconnect();
      }

      if (solProvider && !solanaService.getState().address) {
        void solanaService.connectFromProvider(solProvider);
      }
      if (!solProvider && solanaService.getState().address) {
        solanaService.onDisconnect();
      }
    });

    subscribeToState((state: { open: boolean }) => {
      input.setModalOpen(state.open);
    });

    appKitReady = true;
  }

  // Load cached wallet state when a user logs in
  authManager.subscribe((authState) => {
    if (authState.user != null && authState.user.id !== "guest-local") {
      void walletService.loadCachedState(authState.user.id).then(() => {
        // Auto-sync in background if cache is stale (>6h) and wallet is connected
        const ws = walletService.getState();
        if (ws.address != null && isCacheStale(ws.lastSyncAt)) {
          void walletService.syncNfts(true);
        }
      }).catch((err) => console.warn("[wallet] load cached state failed:", err instanceof Error ? err.message : String(err)));
      void solanaService.loadCachedState(authState.user.id);
    } else if (authState.user == null) {
      // Clear wallet state on sign-out (no method needed â€" WalletService resets on unlink)
    }
  });

  // Load cached state for initial user (if already signed in)
  const initialWalletUserId = authManager.getState().user?.id;
  if (initialWalletUserId != null && initialWalletUserId !== "guest-local") {
    void walletService.loadCachedState(initialWalletUserId).then(() => {
      // Auto-sync in background if cache is stale (>6h) and wallet is connected
      const ws = walletService.getState();
      if (ws.address != null && isCacheStale(ws.lastSyncAt)) {
        void walletService.syncNfts(true);
      }
    }).catch((err) => console.warn("[wallet] load cached state failed:", err instanceof Error ? err.message : String(err)));
    void solanaService.loadCachedState(initialWalletUserId);
  }

  // Wire wallet action callback from state machine (ETH + Solana)
  controller.onWalletAction = (action) => {
    if (action === "solConnect") {
      if (solConnecting) return; // debounce
      solConnecting = true;
      void (async () => {
        try {
          await ensureAppKit();
          const { openWalletModal } = await import("./wallet/appKitService");
          openWalletModal("solana");
        } catch (err) {
          console.warn("[sol] connect error:", err);
        } finally {
          solConnecting = false;
        }
      })();
      return;
    }
    if (action === "solDisconnect") {
      void (async () => {
        try {
          const { disconnectWallet } = await import("./wallet/appKitService");
          await disconnectWallet().catch(() => {});
        } catch { /* AppKit may not be initialized */ }
        try {
          await solanaService.disconnect();
        } catch (err) {
          console.warn("[sol] disconnect error:", err);
        }
      })();
      return;
    }
    if (action === "connect") {
      if (walletConnecting) return; // debounce
      walletConnecting = true;
      void (async () => {
        try {
          await ensureAppKit();
          const { openWalletModal } = await import("./wallet/appKitService");
          openWalletModal("eip155");
        } catch (err) {
          console.warn("[wallet] connect error:", err);
        } finally {
          walletConnecting = false;
        }
      })();
    } else if (action === "unlink") {
      void (async () => {
        try {
          const { disconnectWallet } = await import("./wallet/appKitService");
          await disconnectWallet().catch(() => {});
          await walletService.unlink();
        } catch (err) {
          console.warn("[wallet] unlink error:", err);
        }
      })();
    } else if (action === "sync") {
      if (nftSyncing) return; // debounce
      nftSyncing = true;
      void (async () => {
        try {
          await walletService.syncNfts(false);
        } catch (err) {
          console.warn("[wallet] sync error:", err);
        } finally {
          nftSyncing = false;
        }
      })();
    } else if (action === "avatarSelect") {
      // Equip the selected NFT avatar
      const slotIndex = input.consumeAvatarSelectNftIndex();
      if (slotIndex == null) return;
      const selectedItem = getSelectedProfileItem(slotIndex);
      if (selectedItem == null || selectedItem.kind !== "nft") return;
      const avatarId = selectedItem.targetId;
      const userId = authManager.getState().user?.id;
      const isGuest = userId == null || userId === "guest-local";
      // Equip NFT avatar locally (bypass catalog check â€" NFT avatars aren't in CHARACTER_CATALOG)
      try { localStorage.setItem("bitbeast.equipped.v1", avatarId); } catch { /* ignore */ }
      // Persist to Supabase if logged in
      if (!isGuest) {
        void (async () => {
          try {
            const supabaseModule = await import("./auth/supabaseClient");
            const supabase = supabaseModule.getSupabase();
            await supabase
              .from("profiles")
              .update({ equipped_character: avatarId, updated_at: new Date().toISOString() })
              .eq("id", userId);
          } catch (err) {
            console.warn("[wallet] avatarSelect equip error:", err);
          }
        })();
      }
    }
  };

  // Wire message service: realtime alert callback
  messageService.onNewMessage = (msg) => {
    controller.setIncomingMessageAlert({
      sender: msg.senderName,
      preview: msg.body,
      messageId: msg.id,
      senderSkin: msg.senderSkin,
    });
    void audio.playSfx("button_click");
  };

  // Wire message action callback from state machine
  controller.onMessageAction = (action, messageId) => {
    const msg = messageService.getMessageById(messageId);
    if (!msg) return;

    if (msg.actionType === "accept_decline") {
      const friendId = (msg.actionPayload as Record<string, unknown> | null)?.friend_id as string | undefined;
      if (!friendId) return;
      if (action === "primary") {
        void friendManager.acceptRequest(friendId).then(() => {
          void friendManager.refreshAll();
          void messageService.fetchInbox();
        }).catch((err) => console.warn("[friends] accept request failed:", err instanceof Error ? err.message : String(err)));
      } else {
        void friendManager.declineRequest(friendId).then(() => {
          void friendManager.refreshAll();
          void messageService.fetchInbox();
        }).catch((err) => console.warn("[friends] decline request failed:", err instanceof Error ? err.message : String(err)));
      }
      controller.setPhoneMessageId(null);
      return;
    }

    if (msg.actionType === "navigate") {
      const phase = (msg.actionPayload as Record<string, unknown> | null)?.phase as string | undefined;
      if (phase) {
        controller.phase = phase as import("./game/types").GamePhase;
        controller.setPhoneMessageId(null);
      }
      return;
    }

    if (msg.actionType === "claim_reward") {
      const payload = msg.actionPayload as Record<string, unknown> | null;
      if (payload?.claimed) return;
      const userId = authManager.getState().user?.id;
      if (!userId) return;
      void (async () => {
        const supabase = (await import("./auth/supabaseClient")).getSupabase();
        const { data } = await supabase.functions.invoke("claim-reward", {
          body: { message_id: messageId },
        });
        if (data?.ok) {
          (payload as Record<string, unknown>).claimed = true;
          const { data: wallet } = await supabase.from("user_wallets").select("coins, gems").eq("user_id", userId).single();
          if (wallet) controller.setWalletBalance(wallet.coins, wallet.gems);
        }
      })();
      return;
    }

    if (msg.actionType === "external_link") {
      const url = (msg.actionPayload as Record<string, unknown> | null)?.url as string | undefined;
      if (url) window.open(url, "_blank");
      return;
    }

    // dismiss or no action
    controller.setPhoneMessageId(null);
  };

  /** Format an ISO timestamp as a relative human-readable string (e.g. "2 hours ago"). */
  function formatRelativeTime(isoTimestamp: string | null): string | null {
    if (isoTimestamp == null) return null;
    try {
      const diffMs = Date.now() - new Date(isoTimestamp).getTime();
      const diffSec = Math.floor(diffMs / 1000);
      if (diffSec < 60) return "just now";
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.floor(diffHr / 24);
      return `${diffDay}d ago`;
    } catch {
      return null;
    }
  }

  // Debug state exposure (develop-web-game skill)
  const win = window as unknown as Record<string, unknown>;
  win.render_game_to_text = () => {
    const vm = controller.getViewModel();
    const settingsSurface: SettingsSurface | null = vm.phase === "SETTINGS"
      ? "main"
      : (vm.phase === "PHONE" && vm.phoneTab === "settings" ? "phone" : null);
    const settingsPageDefinition = settingsSurface == null
      ? null
      : getSettingsPageDefinition(
          {
            surface: settingsSurface,
            canUseDebugSettings: vm.canUseDebugSettings
          },
          vm.settingsPage
        );
    const settingsPageDisplayTitle = settingsPageDefinition == null
      ? null
      : (settingsSurface === "main" ? settingsPageDefinition.mainHeading : settingsPageDefinition.phoneTitle);
    return JSON.stringify(
      {
        phase: vm.phase,
        level: vm.run?.level ?? 0,
        levelScore: vm.run?.levelScore ?? 0,
        totalScore: vm.run?.totalScore ?? 0,
        displayScore: vm.displayScore,
        lives: vm.run?.lives ?? 0,
        keys: vm.run?.rotationKeys ?? 0,
        groove: vm.run?.groove ?? null,
        phoneTab: vm.phoneTab,
        leaderboardTab: vm.leaderboardTab,
        selectedSoloLeaderboardMode: vm.selectedSoloLeaderboardMode,
        dailyLoginModalOpen: vm.dailyLoginModalOpen,
        dailyLoginCanClaimToday: vm.dailyLoginStatus?.canClaimToday ?? false,
        dailyLoginClaimableDay: vm.dailyLoginStatus?.claimableDay ?? null,
        warningActive: vm.warningActive,
        pendingRotation: vm.pendingRotation,
        soloMode: vm.soloMode,
        boardSize: vm.run?.board.size ?? null,
        soloCarouselIndex: vm.soloCarouselIndex,
        goalLabel: vm.goalLabel,
        goalProgress: vm.goalProgress,
        goalTarget: vm.goalTarget,
        levelTarget: vm.levelTarget,
        settingsPage: vm.settingsPage,
        settingsPageKey: settingsPageDefinition?.key ?? null,
        settingsPageDisplayTitle,
        animating: !!(vm.rotationAnim?.active || vm.collapseAnim?.active || vm.swapAnim?.active),
        debugViewModel: vm.debugViewModel,
        musicEnabled: vm.musicEnabled,
        gravity: vm.run?.board.gravity ?? null
      },
      null,
      2
    );
  };

  // E2E helper: expose UiRects and wallet for testing
  win.__e2e_getUiRects = () => JSON.stringify(input.getUiRects());
  win.__e2e_dispatchUiAction = (action: UiAction) => {
    controller.dispatchUiActionForTesting(action);
    return true;
  };
  win.__e2e_getWallet = () => {
    const vm = controller.getViewModel();
    return JSON.stringify({ coins: vm.coins, gems: vm.gems });
  };
  win.__e2e_getDailyLogin = () => JSON.stringify({
    status: dailyLoginStatus,
    loading: dailyLoginLoading,
    claimPending: dailyLoginClaimPending
  });
  let latestUiRects: UiRects = {};

  // Prevent any browser-initiated scroll (virtual keyboard, focus, etc.)
  window.addEventListener("scroll", () => window.scrollTo(0, 0));

  // Prevent mobile browser back-swipe gesture (swipe right from left edge).
  // Push a dummy history entry so the gesture pops it instead of navigating away.
  if (!window.history.state?.__bbGuard) {
    window.history.pushState({ __bbGuard: true }, "");
  }
  window.addEventListener("popstate", () => {
    // Re-push the guard state whenever the browser tries to go back
    window.history.pushState({ __bbGuard: true }, "");
  });

  // Block edge-swipe touches so the browser doesn't start the back/forward animation.
  let edgeSwipeBlocked = false;
  const edgeSwipeBlockerOptions = { passive: false, capture: true } as const;
  document.addEventListener("touchstart", (e) => {
    const t = e.touches[0];
    if (t == null) {
      edgeSwipeBlocked = false;
      return;
    }
    const canvasRect = renderer.canvas.getBoundingClientRect();
    const logicalCanvasWidth = controller.phase === "VERSUS_MATCH"
      ? (isMobileViewport() ? VERSUS_MOBILE_CANVAS_WIDTH : VERSUS_CANVAS_WIDTH)
      : currentDims.width;
    if (shouldBlockEdgeSwipeStart({
      phase: controller.phase,
      touchClientX: t.clientX,
      viewportWidth: window.innerWidth,
      boardRect: latestUiRects.board ?? null,
      canvasRect: {
        left: canvasRect.left,
        width: canvasRect.width
      },
      logicalCanvasWidth
    })) {
      edgeSwipeBlocked = true;
      e.preventDefault();
    } else {
      edgeSwipeBlocked = false;
    }
  }, edgeSwipeBlockerOptions);
  document.addEventListener("touchmove", (e) => {
    if (edgeSwipeBlocked) e.preventDefault();
  }, edgeSwipeBlockerOptions);
  document.addEventListener("touchend", () => { edgeSwipeBlocked = false; });
  document.addEventListener("touchcancel", () => { edgeSwipeBlocked = false; });

  // Combined resize handler: sync CSS viewport var + recompute canvas dims on mobile
  // Skip when a text input is focused (keyboard open) to prevent canvas squashing.
  function handleViewportResize(): void {
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      return; // keyboard is open â€" don't resize
    }
    syncViewportHeightVar();
    // Only recompute canvas dimensions for solo mode (not versus which has its own sizing)
    if (controller.phase.startsWith("VERSUS_MATCH")) return;
    const newDims = computeCanvasDimensions();
    if (newDims.width !== currentDims.width || newDims.height !== currentDims.height) {
      currentDims = newDims;
      renderer.setCanvasSize(newDims.width, newDims.height);
      input.setLogicalSize(newDims.width, newDims.height);
    }
    syncMenuSafeAreaInsets(renderer, currentDims);
  }
  window.addEventListener("resize", handleViewportResize);
  if (window.visualViewport != null) {
    window.visualViewport.addEventListener("resize", handleViewportResize);
    window.visualViewport.addEventListener("scroll", handleViewportResize);
  }
  // Safety net for orientation changes that may not fire a resize event on all devices
  if (screen.orientation != null) {
    screen.orientation.addEventListener("change", handleViewportResize);
  }

  const unlock = () => {
    void audio.ensureContext();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);

  // â"€â"€ Versus lobby action handler â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  const versusMenuActionState: VersusMenuActionState = {
    pending: false,
    error: "",
    errorTimerSec: 0,
    phase: "idle",
    activeAction: null,
    retryReadyAction: null,
    statusLine: ""
  };
  win.__e2e_getVersusTransientActionState = () => {
    const vm = controller.getViewModel();
    return JSON.stringify({
      appPhase: vm.phase,
      pending: versusMenuActionState.pending,
      phase: versusMenuActionState.phase,
      activeAction: versusMenuActionState.activeAction,
      retryReadyAction: versusMenuActionState.retryReadyAction,
      statusLine: versusMenuActionState.statusLine,
    });
  };
  const quickmatchAttemptGuard = createQuickmatchAttemptGuard();

  function setVersusMenuError(message: string): void {
    versusMenuActionState.error = message;
    versusMenuActionState.errorTimerSec = 3;
    versusMenuActionState.statusLine = message;
  }

  // Chat state
  let lobbyChatMessages: LobbyChatMessage[] = [];
  let lobbyRealtimeConnected = false;

  // Countdown state
  const COUNTDOWN_DURATION = 3;
  let countdownActive = false;
  let countdownRemaining = 0;
  let countdownMatchCreated = false;

  function subscribeLobbyRealtime(lobbyId: string): void {
    const authState = authManager.getState();
    const profile = authState.profile;
    if (profile == null) return;

    lobbyChatMessages = [];
    lobbyRealtimeConnected = false;

    const equippedSkin = getCachedEquippedCharacter().id ?? "1";
    const playerLevel = profile.level ?? 1;
    lobbyRealtime.subscribe(lobbyId, profile.id, profile.displayName, profile.versusElo, {
      onPlayersChanged: (players) => {
        const lobby = lobbyManager.getCurrentLobby();
        if (lobby != null) {
          lobby.players = players;
        }
        lobbyRealtimeConnected = true;

        // Check both-ready condition (host drives the countdown)
        if (
          controller.phase === "VERSUS_LOBBY" &&
          !countdownActive &&
          players.length === 2 &&
          players.every((p) => p.ready) &&
          lobbyManager.isHost()
        ) {
          startCountdown();
        }
      },
      onChatMessage: (msg) => {
        lobbyChatMessages.push(msg);
        if (lobbyChatMessages.length > 50) {
          lobbyChatMessages = lobbyChatMessages.slice(-50);
        }
      },
      onLobbyStateChanged: (state, matchId, seed) => {
        const lobby = lobbyManager.getCurrentLobby();
        if (lobby != null) {
          lobby.state = state as typeof lobby.state;
          if (matchId != null) lobby.matchId = matchId;
        }
        // Non-host receives COUNTDOWN from host
        if (state === "COUNTDOWN" && !countdownActive) {
          endQuickmatchSearchSession();
          countdownActive = true;
          countdownRemaining = COUNTDOWN_DURATION;
          countdownMatchCreated = false;
          controller.phase = "VERSUS_COUNTDOWN";
        }
        // Both clients receive MATCH_STARTING with seed
        if (state === "MATCH_STARTING" && matchId != null && seed != null) {
          endQuickmatchSearchSession();
          void handleMatchStart(matchId, seed);
        }
      }
    }, equippedSkin, playerLevel);
  }

  function unsubscribeLobbyRealtime(): void {
    lobbyRealtime.unsubscribe();
    lobbyChatMessages = [];
    lobbyRealtimeConnected = false;
    countdownActive = false;
    countdownRemaining = 0;
    countdownMatchCreated = false;
  }

  function subscribeLobbyRealtimeForRematch(lobbyId: string): void {
    const profile = authManager.getState().profile;
    if (profile == null) return;
    const equippedSkin = getCachedEquippedCharacter().id ?? "1";
    const playerLevel = profile.level ?? 1;
    lobbyRealtime.subscribe(lobbyId, profile.id, profile.displayName, profile.versusElo, {
      onPlayersChanged: () => { /* no-op during rematch */ },
      onChatMessage: () => { /* no-op during rematch */ },
      onLobbyStateChanged: (state, matchId, seed) => {
        // If host triggered a rematch countdown → match starting
        if (state === "COUNTDOWN" && controller.phase === "VERSUS_RESULT") {
          controller.phase = "VERSUS_COUNTDOWN";
        }
        if (state === "MATCH_STARTING" && matchId != null && seed != null) {
          void handleMatchStart(matchId, seed);
        }
      },
      onRematchIntent: (_userId, wants) => {
        if (controller.phase === "VERSUS_RESULT") {
          rematchRemoteWants = wants;
          checkRematchBothReady();
        }
      },
      onRematchLeave: () => {
        if (controller.phase === "VERSUS_RESULT") {
          rematchRemoteLeft = true;
        }
      }
    }, equippedSkin, playerLevel);
  }

  function checkRematchBothReady(): void {
    if (!rematchLocalWants || !rematchRemoteWants) return;
    if (controller.phase !== "VERSUS_RESULT") return;

    // Both want rematch — host triggers new countdown
    if (lobbyManager.isHost()) {
      startCountdown();
    }
    // Non-host waits for COUNTDOWN broadcast from host (handled in subscribeLobbyRealtimeForRematch)
  }

  function startCountdown(): void {
    endQuickmatchSearchSession();
    countdownActive = true;
    countdownRemaining = COUNTDOWN_DURATION;
    countdownMatchCreated = false;
    controller.phase = "VERSUS_COUNTDOWN";
    // Update DB state
    void lobbyManager.setLobbyState("COUNTDOWN");
    // Broadcast to non-host
    lobbyRealtime.broadcastLobbyState("COUNTDOWN");
  }

  async function handleCountdownEnd(): Promise<void> {
    if (countdownMatchCreated) return;
    countdownMatchCreated = true;

    if (!lobbyManager.isHost()) return; // Only host creates the match

    const lobby = lobbyManager.getCurrentLobby();
    if (lobby == null) return;
    const players = lobby.players;
    if (players.length < 2) return;

    const matchId = generateLobbyCode() + generateLobbyCode(); // 12-char unique ID
    const seed = seedFromTime();

    const ok = await createMatch({
      matchId,
      lobbyId: lobby.id,
      player1Id: players[0].userId,
      player2Id: players[1].userId,
      seed,
      settings: lobby.settings,
      queueType: lobby.queueType,
      player1AccountLevelBefore: players[0].level,
      player2AccountLevelBefore: players[1].level,
      player1RatingBefore: players[0].elo,
      player2RatingBefore: players[1].elo
    });

    if (ok) {
      void lobbyManager.setLobbyState("MATCH_STARTING", matchId);
      lobbyRealtime.broadcastLobbyState("MATCH_STARTING", matchId, seed);
      // Host also enters match
      void handleMatchStart(matchId, seed);
    } else {
      // Failed to create match â€" go back to lobby
      countdownActive = false;
      controller.phase = "VERSUS_LOBBY";
      setVersusMenuError("FAILED TO START MATCH");
    }
  }

  // â"€â"€ Versus match state â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  let versusResultData: VersusResultData | null = null;
  let matchStartTime = 0;
  let versusStaminaSpentForMatch = false;
  let activeMatchQueueType: LobbyQueueType | null = null;
  // Rematch state
  let rematchLocalWants = false;
  let rematchRemoteWants = false;
  let rematchRemoteLeft = false;

  function handleMatchStart(matchId: string, seed: number): void {
    endQuickmatchSearchSession();
    quickmatchAttemptGuard.invalidate();
    countdownActive = false;
    versusStaminaSpentForMatch = false;
    isBotMatch = false;
    activeBotIdentity = null;
    lastBotCountdownIdentity = null;
    lastBotResultRemoteDisplayName = null;

    // Gather player info from the lobby
    const lobby = lobbyManager.getCurrentLobby();
    const localId = lobbyManager.getLocalUserId();
    if (lobby == null || localId == null || lobby.players.length < 2) {
      setVersusMenuError("MISSING PLAYER DATA");
      controller.phase = "VERSUS_LOBBY";
      return;
    }

    const remotePlayer = lobby.players.find((p) => p.userId !== localId);
    if (remotePlayer == null) {
      setVersusMenuError("OPPONENT NOT FOUND");
      controller.phase = "VERSUS_LOBBY";
      return;
    }

    const localPlayer = lobby.players.find((p) => p.userId === localId);
    const localDisplayName = localPlayer?.displayName ?? "YOU";

    const settings: LobbySettings = lobby.settings ?? {
      startLevel: 1,
      doomClockStartSec: 30,
      trapsEnabled: true
    };
    activeMatchQueueType = lobby.queueType;

    // Clean up lobby realtime (match has its own channel)
    unsubscribeLobbyRealtime();

    // Create the VersusController with dual GameControllers
    versusCtrl = new VersusController(
      audio, authManager, matchId, lobby.id, seed,
      localId, remotePlayer.userId, remotePlayer.displayName, settings,
      {
        onVersusAction: handleVersusAction,
        onMatchEnd: (winnerId, result) => {
          handleMatchEnd(winnerId, result, localId, localDisplayName,
            remotePlayer.displayName, matchId, remotePlayer.userId);
        }
      },
      false,
      undefined,
      remotePlayer.equippedSkin ?? "1"
    );
    const performanceSettings = controller.getPerformanceSettings();
    versusCtrl.localController.setPerformanceSettings(performanceSettings);
    versusCtrl.mirrorController.setPerformanceSettings(performanceSettings);
    versusCtrl.localController.setDebugAccess(currentDebugAccess);
    versusCtrl.mirrorController.setDebugAccess(currentDebugAccess);
    versusCtrl.localController.setDebugSettings(lastDebugSettings, "versus_sync");
    versusCtrl.mirrorController.setDebugSettings(lastDebugSettings, "versus_sync");

    // Set canvas size â€" mobile portrait keeps 360px width, desktop widens for split-screen
    if (isMobileViewport()) {
      const mobileDims = computeCanvasDimensions();
      renderer.setCanvasSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
      input.setLogicalSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
      input.setBoardYOffset(36); // match versusLift in drawMobileVersusMatch
    } else {
      renderer.setCanvasSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
      input.setLogicalSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
      input.setBoardYOffset(0);
    }
    renderer.canvas.classList.add("versus");
    matchStartTime = performance.now() / 1000;
    versusOptionsOpen = false;
    controller.phase = "VERSUS_MATCH";

    // Persist session so we can rejoin after accidental refresh
    if (import.meta.env.DEV) console.log("[rejoin] saving active match session:", matchId);
    saveActiveMatch({
      matchId,
      lobbyId: lobby.id,
      seed,
      localPlayerId: localId,
      remotePlayerId: remotePlayer.userId,
      localSkinId: localPlayer?.equippedSkin ?? "1",
      remoteSkinId: remotePlayer.equippedSkin ?? "1",
    });
  }

  function cleanupVersusMatch(): void {
    if (versusCtrl != null) {
      versusCtrl.destroy();
      versusCtrl = null;
    }
    versusStaminaSpentForMatch = false;
    activeMatchQueueType = null;
    versusOptionsOpen = false;
    renderer.canvas.classList.remove("versus");
    input.setBoardYOffset(0);
    // Restore canvas to dynamic solo dimensions (not hardcoded, for mobile support)
    const soloDims = computeCanvasDimensions();
    renderer.setCanvasSize(soloDims.width, soloDims.height);
    input.setLogicalSize(soloDims.width, soloDims.height);
    currentDims = soloDims;
    syncMenuSafeAreaInsets(renderer, currentDims);
  }

  // ── Versus Rejoin Flow ──────────────────────────────────────────────
  let rejoinOverlayVisible = false;
  let rejoinTimeoutSec = 5;
  let rejoinChecked = false;
  let matchSnapshotTimer = 0;

  function tryRejoinActiveMatch(): boolean {
    const session = loadActiveMatch();
    if (import.meta.env.DEV) console.log("[rejoin] checking for active match:", session);
    if (session == null) return false;
    const authState = authManager.getState();
    if (authState.user == null || authState.user.id !== session.localPlayerId) {
      clearActiveMatch();
      return false;
    }

    // Bot matches: restore from saved snapshot
    if (session.matchId.startsWith("bot-")) {
      const snapshot = loadMatchSnapshot();
      if (snapshot == null) {
        if (import.meta.env.DEV) console.log("[rejoin] bot match — no snapshot, recording forfeit loss");
        const profile = authState.profile;
        const localElo = profile?.versusElo ?? 1000;
        if (profile != null && isPersistentAccount(authState.user)) {
          void updateBotMatchRating(profile.id, localElo, localElo, false).then(() => {
            void authManager.refreshProfile();
          }).catch((err) => console.warn("[rejoin] bot forfeit rating update failed:", err instanceof Error ? err.message : String(err)));
        }
        clearActiveMatch();
        return false;
      }

      if (import.meta.env.DEV) console.log("[rejoin] bot match — restoring from snapshot");
      const profile = authState.profile;
      const skillProfile = createBotSkillProfile({
        versusElo: profile?.versusElo,
        level: profile?.level,
        versusPlayed: profile?.versusPlayed,
        momentum: localVersusMomentum
      });
      botPlayer = new BotPlayer(undefined, skillProfile);
      activeBotIdentity = botPlayer.identity;
      isBotMatch = true;
      activeMatchQueueType = null;

      const settings: LobbySettings = { startLevel: 1, doomClockStartSec: 30, trapsEnabled: true };
      versusCtrl = new VersusController(
        audio, authManager, session.matchId, null, session.seed,
        session.localPlayerId, session.remotePlayerId, "BOT", settings,
        {
          onVersusAction: handleVersusAction,
          onMatchEnd: (winnerId, result) => {
            handleMatchEnd(winnerId, result, session.localPlayerId,
              profile?.displayName ?? "YOU", "BOT", session.matchId, session.remotePlayerId);
          }
        },
        true,
        botPlayer.input,
        session.remoteSkinId
      );
      const performanceSettings = controller.getPerformanceSettings();
      versusCtrl.localController.setPerformanceSettings(performanceSettings);
      versusCtrl.mirrorController.setPerformanceSettings(performanceSettings);

      // Restore run states from snapshot
      versusCtrl.localController.forceSyncRunSnapshot(snapshot.localRun as never);
      versusCtrl.mirrorController.forceSyncRunSnapshot(snapshot.mirrorRun as never);

      // Set canvas for versus
      if (isMobileViewport()) {
        const mobileDims = computeCanvasDimensions();
        renderer.setCanvasSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
        input.setLogicalSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
        input.setBoardYOffset(36);
      } else {
        renderer.setCanvasSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
        input.setLogicalSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
        input.setBoardYOffset(0);
      }
      renderer.canvas.classList.add("versus");
      matchStartTime = performance.now() / 1000;
      versusOptionsOpen = false;
      controller.phase = "VERSUS_MATCH";
      return true;
    }

    rejoinOverlayVisible = true;
    rejoinTimeoutSec = 5;

    const sync = new MatchRealtimeSync();
    let resolved = false;

    const cleanup = (): void => {
      resolved = true;
      rejoinOverlayVisible = false;
      sync.unsubscribe();
    };

    const failRejoin = (reason?: string): void => {
      if (resolved) return;
      cleanup();
      clearActiveMatch();
      if (import.meta.env.DEV) console.log("[rejoin] failed:", reason);
      controller.phase = "TITLE";
    };

    // Timeout after 5 seconds
    const timeoutId = window.setTimeout(() => failRejoin("timeout"), 5000);
    const tickInterval = window.setInterval(() => {
      rejoinTimeoutSec = Math.max(0, rejoinTimeoutSec - 0.1);
    }, 100);

    sync.subscribe(
      session.matchId,
      session.localPlayerId,
      session.remotePlayerId,
      {
        onRemoteEvent: (event) => {
          if (resolved) return;
          if (event.type === "REJOIN_SNAPSHOT") {
            window.clearTimeout(timeoutId);
            window.clearInterval(tickInterval);
            cleanup();

            const payload = event.payload as {
              opponentRun: unknown;
              rejoinRun: unknown;
            };

            // Build a VersusController like a normal match start
            const settings: LobbySettings = { startLevel: 1, doomClockStartSec: 30, trapsEnabled: true };
            versusCtrl = new VersusController(
              audio, authManager, session.matchId, session.lobbyId, session.seed,
              session.localPlayerId, session.remotePlayerId, "OPPONENT", settings,
              {
                onVersusAction: handleVersusAction,
                onMatchEnd: (winnerId, result) => {
                  handleMatchEnd(winnerId, result, session.localPlayerId, authState.profile?.displayName ?? "YOU",
                    "OPPONENT", session.matchId, session.remotePlayerId);
                }
              },
              false,
              undefined,
              session.remoteSkinId
            );
            const performanceSettings = controller.getPerformanceSettings();
            versusCtrl.localController.setPerformanceSettings(performanceSettings);
            versusCtrl.mirrorController.setPerformanceSettings(performanceSettings);

            // Restore run states from opponent's snapshot
            if (payload.rejoinRun != null) {
              versusCtrl.localController.forceSyncRunSnapshot(payload.rejoinRun as never);
            }
            if (payload.opponentRun != null) {
              versusCtrl.mirrorController.forceSyncRunSnapshot(payload.opponentRun as never);
            }

            // Set canvas for versus
            if (isMobileViewport()) {
              const mobileDims = computeCanvasDimensions();
              renderer.setCanvasSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
              input.setLogicalSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
              input.setBoardYOffset(36);
            } else {
              renderer.setCanvasSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
              input.setLogicalSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
              input.setBoardYOffset(0);
            }
            renderer.canvas.classList.add("versus");
            matchStartTime = performance.now() / 1000;
            versusOptionsOpen = false;
            activeMatchQueueType = "quickmatch";
            controller.phase = "VERSUS_MATCH";
          }
        },
        onRemoteDisconnect: () => failRejoin("opponent disconnected"),
        onRemoteReconnect: () => {},
        onChannelStatusChanged: () => {},
        onSendFailure: () => failRejoin("send failure"),
      },
      () => ({ heartbeatSeq: 0, ackGameplaySeq: 0, score: 0, level: 1, levelProgressPct: 0, equippedSkin: "1", doomPct: 0, groovePct: 0, grooveActive: false, grooveReady: false, pendingRotation: null, rotationKeys: 0, cash: 0 })
    );

    // Send rejoin request once channel is ready
    window.setTimeout(() => {
      if (!resolved) {
        sync.sendEvent("REJOIN_REQUEST", {});
      }
    }, 500);

    return true;
  }

  // Cancel rejoin (user tapped Cancel) — forfeit the match
  function cancelRejoin(): void {
    rejoinOverlayVisible = false;
    clearActiveMatch();
    controller.phase = "TITLE";
  }

  // Bot match state
  let botPlayer: BotPlayer | null = null;
  let activeBotIdentity: BotIdentity | null = null;
  let lastBotCountdownIdentity: BotIdentity | null = null;
  let lastBotResultRemoteDisplayName: string | null = null;
  let isBotMatch = false;
  let botSearchTimer = 0;
  let lobbyWaitForOpponentSec = 0;
  let localVersusMomentum = 0;
  let quickmatchSearchActive = false;
  const BOT_SEARCH_TIMEOUT_SEC = 7;

  function beginQuickmatchSearchSession(): void {
    quickmatchSearchActive = true;
    botSearchTimer = 0;
    lobbyWaitForOpponentSec = 0;
  }

  function endQuickmatchSearchSession(): void {
    quickmatchSearchActive = false;
    botSearchTimer = 0;
    lobbyWaitForOpponentSec = 0;
  }

  win.__e2e_getVersusFallbackSnapshot = () => {
    const vm = controller.getViewModel();
    return JSON.stringify({
      phase: vm.phase,
      isBotMatch,
      botIdentity: activeBotIdentity,
      countdownIdentityUserId: lastBotCountdownIdentity?.userId ?? null,
      resultIdentityDisplayName: lastBotResultRemoteDisplayName,
      countdownOpponentUsername: vm.versusCountdown?.opponentUsername ?? null,
      countdownOpponentSkin: vm.versusCountdown?.opponentSkin ?? null,
      countdownOpponentLevel: vm.versusCountdown?.opponentLevel ?? null,
      resultRemoteDisplayName: vm.versusResult?.remoteDisplayName ?? null
    });
  };

  win.__e2e_forceVersusForfeit = () => {
    if (controller.phase !== "VERSUS_MATCH" || versusCtrl == null) {
      return false;
    }
    versusCtrl.forfeit();
    return true;
  };

  function leaveLobbyPhase(): void {
    endQuickmatchSearchSession();
    quickmatchAttemptGuard.invalidate();
    unsubscribeLobbyRealtime();
    controller.setVersusBusy(false);
    void lobbyManager.leaveLobby().then(() => {
      controller.phase = "TITLE";
    }).catch((err) => {
      controller.phase = "TITLE";
      console.warn("[lobby] leave failed:", err instanceof Error ? err.message : String(err));
    });
  }

  function startBotMatch(): void {
    endQuickmatchSearchSession();
    quickmatchAttemptGuard.invalidate();
    const profile = authManager.getState().profile;
    const skillProfile = createBotSkillProfile({
      versusElo: profile?.versusElo,
      level: profile?.level,
      versusPlayed: profile?.versusPlayed,
      momentum: localVersusMomentum
    });
    botPlayer = new BotPlayer(undefined, skillProfile);
    activeBotIdentity = botPlayer.identity;
    lastBotCountdownIdentity = null;
    lastBotResultRemoteDisplayName = null;
    isBotMatch = true;
    activeMatchQueueType = null;
    botSearchTimer = 0;
    controller.setVersusBusy(false);

    const localId = profile?.id ?? "guest-local";
    const localDisplayName = profile?.displayName ?? "PLAYER";
    const localSkin = getCachedEquippedCharacter().id ?? "1";
    const localUsername = profile?.username ?? "PLAYER";
    const localLevel = profile?.level ?? 1;
    const seed = seedFromTime();
    const matchId = `bot-${Date.now()}`;
    const settings: LobbySettings = { startLevel: 1, doomClockStartSec: 30, trapsEnabled: true };

    // Show countdown with bot info
    countdownActive = true;
    countdownRemaining = COUNTDOWN_DURATION;
    countdownMatchCreated = false;
    controller.phase = "VERSUS_COUNTDOWN";

    // Store data for the countdown render and match start
    botMatchPending = {
      matchId, seed, localId, localDisplayName, localSkin, localUsername, localLevel,
      botIdentity: activeBotIdentity, settings
    };
  }

  function startOnboardingBotMatch(): void {
    endQuickmatchSearchSession();
    quickmatchAttemptGuard.invalidate();
    const skillProfile = createBotSkillProfile({
      versusElo: 0, level: 1, versusPlayed: 0, momentum: -1,
    });
    botPlayer = new BotPlayer(RAFSBY_BOT_IDENTITY, skillProfile);
    activeBotIdentity = botPlayer.identity;
    lastBotCountdownIdentity = null;
    lastBotResultRemoteDisplayName = null;
    isBotMatch = true;
    activeMatchQueueType = null;
    botSearchTimer = 0;
    controller.setVersusBusy(false);

    const profile = authManager.getState().profile;
    const localId = profile?.id ?? "guest-local";
    const localDisplayName = profile?.displayName ?? "PLAYER";
    const localSkin = getCachedEquippedCharacter().id ?? "1";
    const localUsername = profile?.username ?? "PLAYER";
    const localLevel = profile?.level ?? 1;
    const seed = seedFromTime();
    const matchId = "onboarding-" + Date.now();
    const settings: LobbySettings = { startLevel: 1, doomClockStartSec: 30, trapsEnabled: false };

    countdownActive = true;
    countdownRemaining = COUNTDOWN_DURATION;
    countdownMatchCreated = false;
    controller.phase = "VERSUS_COUNTDOWN";

    botMatchPending = {
      matchId, seed, localId, localDisplayName, localSkin, localUsername, localLevel,
      botIdentity: activeBotIdentity, settings
    };
  }

  let botMatchPending: {
    matchId: string; seed: number; localId: string; localDisplayName: string;
    localSkin: string; localUsername: string; localLevel: number;
    botIdentity: BotIdentity; settings: LobbySettings;
  } | null = null;

  function handleBotMatchStart(): void {
    if (botMatchPending == null || botPlayer == null) return;
    const { matchId, seed, localId, localDisplayName, localSkin, settings, botIdentity } = botMatchPending;

    countdownActive = false;
    versusStaminaSpentForMatch = false;
    activeMatchQueueType = null;

    // Create VersusController â€" bot matches skip the realtime sync (offline=true)
    versusCtrl = new VersusController(
      audio, authManager, matchId, null, seed,
      localId, botIdentity.userId, botIdentity.displayName, settings,
      {
        onVersusAction: handleVersusAction,
        onMatchEnd: (winnerId, result) => {
          handleMatchEnd(winnerId, result, localId, localDisplayName,
            botIdentity.displayName, matchId, botIdentity.userId);
        }
      },
      true,             // offline â€" no network sync for bot matches
      botPlayer!.input,  // wire bot adapter as mirror input
      botIdentity.equippedSkin ?? "1"
    );
    const performanceSettings = controller.getPerformanceSettings();
    versusCtrl.localController.setPerformanceSettings(performanceSettings);
    versusCtrl.mirrorController.setPerformanceSettings(performanceSettings);
    versusCtrl.localController.setDebugAccess(currentDebugAccess);
    versusCtrl.mirrorController.setDebugAccess(currentDebugAccess);
    versusCtrl.localController.setDebugSettings(lastDebugSettings, "versus_sync");
    versusCtrl.mirrorController.setDebugSettings(lastDebugSettings, "versus_sync");

    if (isMobileViewport()) {
      const mobileDims = computeCanvasDimensions();
      renderer.setCanvasSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
      input.setLogicalSize(VERSUS_MOBILE_CANVAS_WIDTH, mobileDims.height);
      input.setBoardYOffset(36); // match versusLift in drawMobileVersusMatch
    } else {
      renderer.setCanvasSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
      input.setLogicalSize(VERSUS_CANVAS_WIDTH, VERSUS_CANVAS_HEIGHT);
      input.setBoardYOffset(0);
    }
    renderer.canvas.classList.add("versus");
    matchStartTime = performance.now() / 1000;
    versusOptionsOpen = false;
    controller.phase = "VERSUS_MATCH";
    botMatchPending = null;

    // Persist session so accidental refresh counts as forfeit (affects rank)
    if (import.meta.env.DEV) console.log("[rejoin] saving bot match session:", matchId);
    saveActiveMatch({
      matchId,
      lobbyId: "",
      seed,
      localPlayerId: localId,
      remotePlayerId: botIdentity.userId,
      localSkinId: localSkin,
      remoteSkinId: botIdentity.equippedSkin ?? "1",
    });
  }

  function handleMatchEnd(
    winnerId: string | null, result: string,
    localId: string, localDisplayName: string,
    remoteDisplayName: string, matchId: string, remoteId: string
  ): void {
    const elapsedSec = performance.now() / 1000 - matchStartTime;
    const localStats = versusCtrl?.getLocalFinalStats() ?? { score: 0, level: 1 };
    const remoteStats = versusCtrl?.getRemoteFinalStats() ?? { score: 0, level: 1 };
    const localWon = winnerId === localId;
    const scoreDelta = localStats.score - remoteStats.score;
    const scoreSwing = clamp(scoreDelta / 4000, -1, 1);
    const outcomeMomentum = winnerId == null ? scoreSwing * 0.5 : (localWon ? 0.7 : -0.7) + (scoreSwing * 0.3);
    localVersusMomentum = clamp((localVersusMomentum * 0.6) + (outcomeMomentum * 0.4), -1, 1);
    const baseReward = localWon ? RETENTION_REWARD_TABLE.versusWin : RETENTION_REWARD_TABLE.versusLoss;
    const firstWinBonusReady = localWon && canClaimFirstWinBonus();
    const firstWinBonusReward = firstWinBonusReady
      ? RETENTION_REWARD_TABLE.firstWinOfDayBonus
      : { gems: 0 };
    const authState = authManager.getState();
    const profile = authState.profile;
    const localElo = profile?.versusElo ?? 1000;
    const opponentElo = isBotMatch
      ? localElo
      : (lobbyManager.getOpponent()?.elo ?? localElo);
    const isGuest = !isPersistentAccount(authState.user);
    const baseXpAmount = versusRunXp({
      localFinalLevel: localStats.level,
      localScore: localStats.score,
      won: localWon,
      localElo,
      opponentElo,
      isBotMatch
    });
    const xpAmount = isGuest
      ? 0
      : baseXpAmount + (firstWinBonusReady ? FIRST_WIN_DAILY_XP_BONUS : 0);
    const gemsAmount = baseReward.gems + firstWinBonusReward.gems;

    versusResultData = {
      localWon,
      draw: winnerId == null,
      result: result as MatchResult,
      localDisplayName,
      remoteDisplayName,
      localScore: localStats.score,
      remoteScore: remoteStats.score,
      localLevel: localStats.level,
      remoteLevel: remoteStats.level,
      elapsedSec,
      gemsWon: gemsAmount,
      xpGained: xpAmount,
      ratingBefore: localElo,
      tierBefore: rankedTierForRating(localElo),
      localSkin: getCachedEquippedCharacter().id ?? "1",
      remoteSkin: versusCtrl?.getMatchState().opponentStats.equippedSkin ?? "1",
    };

    if (isBotMatch) {
      lastBotResultRemoteDisplayName = remoteDisplayName;
      // Update real Elo in DB for bot matches + refresh profile so animation triggers
      const botUserId = authState.user?.id;
      if (botUserId != null && isPersistentAccount(authState.user)) {
        void updateBotMatchRating(botUserId, localElo, opponentElo, localWon).then(() => {
          void authManager.refreshProfile().catch(() => {});
        }).catch((err) => console.warn("[versus] bot match rating update failed:", err instanceof Error ? err.message : String(err)));
      }
    }

    // Award gems + XP
    const userId = authState.user?.id;
    if (gemsAmount > 0) {
      if (isGuest) {
        if (firstWinBonusReady) {
          claimFirstWinBonusLocal();
        }
        creditGemsLocal(baseReward.gems);
      } else if (userId != null) {
        if (firstWinBonusReady) {
          void claimFirstWinBonus(userId);
        }
        void creditGems(userId, baseReward.gems, "versus_win");
      }
    }
    if (isGuest) {
      // no-op for guest XP
    } else {
      void awardPersistentXp(xpAmount);
    }

    // Persist match results for non-bot matches
    if (!isBotMatch) {
      void finalizeMatch(
        matchId, winnerId, result as MatchResult,
        localId, localStats.score, localStats.level,
        remoteId, remoteStats.score, remoteStats.level, elapsedSec
      );
      if (versusCtrl != null) {
        void submitMatchSummary(versusCtrl.buildMatchSummary(elapsedSec));
      }
      // Don't close lobby yet — keep it alive for rematch signaling
      if (activeMatchQueueType === "quickmatch") {
        scheduleRankedProfileRefresh();
      }
    }

    if (!versusStaminaSpentForMatch) {
      versusStaminaSpentForMatch = true;
      void staminaService.consumeRound();
    }

    // Reset rematch state
    rematchLocalWants = false;
    rematchRemoteWants = false;
    rematchRemoteLeft = false;

    // Re-subscribe to lobby realtime for rematch signaling (non-bot only)
    if (!isBotMatch) {
      const lobby = lobbyManager.getCurrentLobby();
      if (lobby != null) {
        subscribeLobbyRealtimeForRematch(lobby.id);
      }
    } else {
      // Bot "leaves" after a short delay so it looks like a real player who left
      setTimeout(() => {
        if (controller.phase === "VERSUS_RESULT" && isBotMatch) {
          rematchRemoteLeft = true;
        }
      }, 1500 + Math.random() * 2000);
    }

    // Restore canvas to dynamic solo dimensions (not hardcoded, for mobile support)
    versusOptionsOpen = false;
    const soloDims = computeCanvasDimensions();
    renderer.setCanvasSize(soloDims.width, soloDims.height);
    input.setLogicalSize(soloDims.width, soloDims.height);
    currentDims = soloDims;
    syncMenuSafeAreaInsets(renderer, currentDims);
    controller.resetVersusResultStep();
    controller.phase = "VERSUS_RESULT";
  }

  async function awardPersistentXp(amount: number): Promise<void> {
    if (amount <= 0) {
      return;
    }
    const authState = authManager.getState();
    const userId = authState.user?.id;
    if (!isPersistentAccount(authState.user) || userId == null) {
      return;
    }
    await authManager.addXp(amount);
    const currentLevel = authManager.getState().profile?.level ?? 1;
    const rewarded = await referralService.checkAndRewardReferral(userId, currentLevel);
    if (rewarded) {
      const balance = await fetchBalance(userId);
      if (balance != null) {
        controller.setWalletBalance(balance.coins, balance.gems);
      }
    }
  }

  function scheduleRankedProfileRefresh(): void {
    if (!isPersistentAccount(authManager.getState().user)) {
      return;
    }
    const refreshDelaysMs = [250, 1500, 3500];
    for (const delayMs of refreshDelaysMs) {
      setTimeout(() => {
        if (!isPersistentAccount(authManager.getState().user)) {
          return;
        }
        void authManager.refreshProfile().catch(() => {});
        if (controller.getLeaderboardTab() === "versus" || controller.phase === "TITLE" || controller.phase === "VERSUS_LOBBY") {
          void refreshVersusLeaderboard().catch(() => {});
        }
      }, delayMs);
    }
  }

  /** Clipboard copy with fallback for non-HTTPS contexts (e.g. LAN dev server). */
  function copyToClipboard(text: string): void {
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).catch(() => {/* no-op */});
      return;
    }
    // Fallback: hidden textarea + execCommand
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch { /* no-op */ }
    document.body.removeChild(ta);
  }

  function handleVersusAction(action: UiAction): void {
    if (action === "OPEN_SETTINGS" && controller.phase === "VERSUS_MATCH") {
      versusOptionsOpen = !versusOptionsOpen;
      return;
    }
    // Forfeit versus match from in-match options
    if (action === "VERSUS_FORFEIT") {
      versusOptionsOpen = false;
      if (versusCtrl != null) {
        versusCtrl.forfeit();
        return;
      }
      cleanupVersusMatch();
      controller.phase = "TITLE";
      return;
    }
    if (action === "VERSUS_MODE" && controller.phase === "TITLE") {
      if (!multiplayerBackendAvailable) {
        controller.phase = "VERSUS_LOBBY";
        controller.setVersusBusy(false);
        controller.setVersusError("MULTIPLAYER UNAVAILABLE");
        lobbyBrowserError = "MULTIPLAYER UNAVAILABLE";
        return;
      }

      endQuickmatchSearchSession();
      quickmatchAttemptGuard.invalidate();
      controller.setVersusBusy(false);
      controller.setVersusError("");
      lobbyBrowserError = "";
      lobbyJoinInputOpen = false;

      void refreshOpenLobbies();

      const existingLobby = lobbyManager.getCurrentLobby();
      if (existingLobby != null) {
        unsubscribeLobbyRealtime();
        controller.phase = "VERSUS_LOBBY";
        subscribeLobbyRealtime(existingLobby.id);
        return;
      }

      controller.phase = "VERSUS_LOBBY";
      return;
    }

    // Quick match from title screen
    if (action === "VERSUS_QUICKMATCH" && controller.phase === "TITLE") {
      if (!staminaService.canPlay()) {
        controller.pendingGameplayIntent = { kind: "versus_quickmatch" };
        void adService.showAd().then(() => {
          void controller.restoreStaminaFromAd();
        }).catch((err) => console.warn("[ad] showAd failed:", err instanceof Error ? err.message : String(err)));
        return;
      }
      if (!multiplayerBackendAvailable) {
        // No backend â€" go straight to bot match
        startBotMatch();
        return;
      }
      const quickmatchAttemptId = quickmatchAttemptGuard.beginAttempt();
      // Enter dedicated battle search screen while matchmaking.
      controller.phase = "VERSUS_LOBBY";
      controller.setVersusBusy(true);
      controller.setVersusError("");
      beginQuickmatchSearchSession();
      void runVersusMenuAction({
        state: versusMenuActionState,
        actionKey: "VERSUS_QUICKMATCH",
        task: async () => {
          const result = await quickmatch(lobbyManager);
          if (result.error?.startsWith("NETWORK ")) {
            throw new Error(result.error);
          }
          return result;
        },
        fallbackError: "FAILED TO QUICK MATCH",
        onFailure: (message) => {
          endQuickmatchSearchSession();
          if (controller.phase !== "TITLE" && controller.phase !== "VERSUS_LOBBY") {
            return;
          }
          controller.phase = "TITLE";
          controller.setVersusBusy(false);
          controller.setVersusError(message);
        },
        onSuccess: (result) => {
          if (!quickmatchAttemptGuard.isCurrentAttempt(quickmatchAttemptId)) {
            return;
          }
          if (result.error) {
            endQuickmatchSearchSession();
            controller.phase = "TITLE";
            controller.setVersusBusy(false);
            controller.setVersusError(result.error);
            versusMenuActionState.phase = "failed_ready_to_retry";
            versusMenuActionState.activeAction = "VERSUS_QUICKMATCH";
            versusMenuActionState.retryReadyAction = "VERSUS_QUICKMATCH";
            versusMenuActionState.error = result.error;
            versusMenuActionState.errorTimerSec = 3;
            versusMenuActionState.statusLine = result.error;
            return;
          }
          if (result.lobbyId != null) {
            endQuickmatchSearchSession();
            controller.phase = "VERSUS_LOBBY";
            controller.setVersusBusy(true);
            subscribeLobbyRealtime(result.lobbyId);
            // Auto-ready the local player
            const profile = authManager.getState().profile;
            const equippedSkin = getCachedEquippedCharacter().id ?? "1";
            const level = profile?.level ?? 1;
            void lobbyManager.toggleReady().then(() => {
              if (profile != null) {
                void lobbyRealtime.trackReady(true, profile.displayName, profile.versusElo, equippedSkin, level);
              }
            }).catch((err) => console.warn("[lobby] toggle ready failed:", err instanceof Error ? err.message : String(err)));
            return;
          }
          endQuickmatchSearchSession();
          controller.phase = "TITLE";
          controller.setVersusBusy(false);
          controller.setVersusError("FAILED TO QUICK MATCH");
          versusMenuActionState.phase = "failed_ready_to_retry";
          versusMenuActionState.activeAction = "VERSUS_QUICKMATCH";
          versusMenuActionState.retryReadyAction = "VERSUS_QUICKMATCH";
          versusMenuActionState.error = "FAILED TO QUICK MATCH";
          versusMenuActionState.errorTimerSec = 3;
          versusMenuActionState.statusLine = "FAILED TO QUICK MATCH";
        }
      });
      return;
    }
    if (action === "VERSUS_CREATE_LOBBY" && controller.phase === "VERSUS_LOBBY") {
      if (!multiplayerBackendAvailable) {
        controller.setVersusError("MULTIPLAYER UNAVAILABLE");
        return;
      }
      const authState = authManager.getState();
      const accountLevel = authState.profile?.level ?? 1;
      if (!isFeatureUnlocked(accountLevel, "customLobbies")) {
        const requiredLevel = featureUnlockLevel("customLobbies");
        controller.setVersusError(
          isPersistentAccount(authState.user)
            ? `UNLOCKS AT LV ${requiredLevel}`
            : `SIGN IN AND REACH LV ${requiredLevel}`
        );
        return;
      }
      controller.setVersusBusy(true);
      controller.setVersusError("");
      void lobbyManager.createAndJoinLobby().then(({ lobbyId, error }) => {
        if (error != null || lobbyId == null) {
          controller.setVersusBusy(false);
          lobbyBrowserError = error ?? "FAILED TO CREATE LOBBY";
          return;
        }
        controller.setVersusBusy(false);
        lobbyBrowserError = "";
        subscribeLobbyRealtime(lobbyId);
        void refreshOpenLobbies();
      }).catch((err) => {
        controller.setVersusBusy(false);
        console.warn("[lobby] create failed:", err instanceof Error ? err.message : String(err));
      });
      return;
    }
    if (action === "LOBBY_TOGGLE_JOIN" && controller.phase === "VERSUS_LOBBY") {
      lobbyJoinInputOpen = !lobbyJoinInputOpen;
      return;
    }
    if (action === "VERSUS_JOIN_LOBBY" && controller.phase === "VERSUS_LOBBY") {
      if (!multiplayerBackendAvailable) {
        controller.setVersusError("MULTIPLAYER UNAVAILABLE");
        return;
      }
      const authState = authManager.getState();
      const accountLevel = authState.profile?.level ?? 1;
      if (!isFeatureUnlocked(accountLevel, "customLobbies")) {
        const requiredLevel = featureUnlockLevel("customLobbies");
        controller.setVersusError(
          isPersistentAccount(authState.user)
            ? `UNLOCKS AT LV ${requiredLevel}`
            : `SIGN IN AND REACH LV ${requiredLevel}`
        );
        return;
      }
      const code = lobbyCodeInput.value.trim().toUpperCase();
      if (code.length < 4) {
        lobbyBrowserError = "ENTER LOBBY CODE";
        return;
      }
      controller.setVersusBusy(true);
      controller.setVersusError("");
      void lobbyManager.joinExistingLobby(code).then(({ error }) => {
        if (error != null) {
          controller.setVersusBusy(false);
          lobbyBrowserError = error;
          return;
        }
        controller.setVersusBusy(false);
        lobbyBrowserError = "";
        lobbyCodeInput.value = "";
        subscribeLobbyRealtime(code);
        void refreshOpenLobbies();
      }).catch((err) => {
        controller.setVersusBusy(false);
        console.warn("[lobby] join failed:", err instanceof Error ? err.message : String(err));
      });
      return;
    }
    if (action === "VERSUS_TAP_LOBBY_ROW" && controller.phase === "VERSUS_LOBBY") {
      const idx = input.consumeTappedLobbyIndex();
      if (idx == null || idx < 0 || idx >= openLobbies.length) return;
      if (!multiplayerBackendAvailable) {
        controller.setVersusError("MULTIPLAYER UNAVAILABLE");
        return;
      }
      const code = openLobbies[idx].id;
      controller.setVersusBusy(true);
      controller.setVersusError("");
      void lobbyManager.joinExistingLobby(code).then(({ error }) => {
        if (error != null) {
          controller.setVersusBusy(false);
          lobbyBrowserError = error;
          return;
        }
        controller.setVersusBusy(false);
        lobbyBrowserError = "";
        lobbyCodeInput.value = "";
        subscribeLobbyRealtime(code);
        void refreshOpenLobbies();
      }).catch((err) => {
        controller.setVersusBusy(false);
        console.warn("[lobby] join failed:", err instanceof Error ? err.message : String(err));
      });
      return;
    }
    if (action === "VERSUS_REFRESH_LOBBIES" && controller.phase === "VERSUS_LOBBY") {
      void refreshOpenLobbies();
      return;
    }
    if (action === "VERSUS_READY" && controller.phase === "VERSUS_LOBBY") {
      const currentPlayer = lobbyManager.getLocalPlayer();
      const intendedReady = currentPlayer != null ? !currentPlayer.ready : true;
      if (intendedReady && !staminaService.canPlay()) {
        controller.pendingGameplayIntent = { kind: "versus_return", returnPhase: "VERSUS_LOBBY" };
        void adService.showAd().then(() => {
          void controller.restoreStaminaFromAd();
        }).catch((err) => console.warn("[ad] showAd failed:", err instanceof Error ? err.message : String(err)));
        return;
      }
      const profile = authManager.getState().profile;
      const equippedSkin = getCachedEquippedCharacter().id ?? "1";
      const level = profile?.level ?? 1;
      void lobbyManager.toggleReady().then(() => {
        if (profile != null) {
          void lobbyRealtime.trackReady(intendedReady, profile.displayName, profile.versusElo, equippedSkin, level);
        }
      }).catch((err) => console.warn("[lobby] toggle ready failed:", err instanceof Error ? err.message : String(err)));
      return;
    }
    if (action === "VERSUS_CHAT" && controller.phase === "VERSUS_LOBBY") {
      sendLobbyChatFromInput();
      return;
    }
    if (action === "VERSUS_LEAVE") {
      if (controller.phase === "VERSUS_LOBBY" && controller.getViewModel().versusBusy) {
        endQuickmatchSearchSession();
        quickmatchAttemptGuard.invalidate();
        unsubscribeLobbyRealtime();
        controller.setVersusBusy(false);
        void lobbyManager.leaveLobby();
        controller.phase = "TITLE";
        return;
      }
      endQuickmatchSearchSession();
      leaveLobbyPhase();
      return;
    }

    if (action === "CLOSE_DAILY_LOGIN_BONUS" && controller.phase === "TITLE") {
      const userId = authManager.getState().user?.id;
      if (userId != null && userId !== "guest-local" && dailyLoginStatus?.canClaimToday) {
        markDailyLoginBonusDismissed(userId, dailyLoginStatus.todayUtc);
      }
      return;
    }

    if (action === "CLAIM_DAILY_LOGIN_BONUS" && (controller.phase === "TITLE" || controller.phase === "DAILY_REWARDS" || controller.phase === "DAILY_REWARDS_OPEN")) {
      const userId = authManager.getState().user?.id;
      if (userId == null || userId === "guest-local" || dailyLoginClaimPending) {
        return;
      }

      dailyLoginClaimPending = true;
      clearDailyLoginBonusDismissal(userId);
      // Pre-populate reward from known table so animation shows it immediately
      if (dailyLoginStatus?.claimableDay != null) {
        const knownReward = getDailyLoginReward(dailyLoginStatus.claimableDay);
        if (knownReward) {
          controller.setDailyRewardClaimed(knownReward.coins, knownReward.gems);
        }
      }
      void claimDailyLoginBonus(userId)
        .then(async (result) => {
          if (result != null) {
            dailyLoginStatus = result.status;
            controller.setWalletBalance(result.balance.coins, result.balance.gems);
            controller.setDailyRewardClaimed(result.reward.coins, result.reward.gems);
            controller.triggerGemBurst();
            return;
          }
          dailyLoginStatus = await refreshDailyLoginStatus(userId);
        })
        .catch((err) => console.warn("[daily-login] claim failed:", err instanceof Error ? err.message : String(err)))
        .finally(() => {
          dailyLoginClaimPending = false;
        });
      return;
    }

    // Message inbox row tap
    if (action === "OPEN_MESSAGE") {
      const idx = input.consumeTappedInboxIndex();
      if (idx < 0) return;
      const inbox = messageService.getCachedInbox();
      const msg = inbox[idx];
      if (!msg) return;
      controller.setPhoneMessageId(msg.id);
      void messageService.markRead(msg.id);
      return;
    }

    // Friend row tap — open detail view
    if (action === "OPEN_FRIEND_DETAIL") {
      const idx = input.consumeTappedFriendIndex();
      if (idx < 0) return;
      const allFriends = [{ ...RAFSBY_NPC }, ...friendManager.getCachedFriends()];
      const pageStart = friendsPage * 5;
      const f = allFriends[pageStart + idx];
      if (!f) return;
      controller.setPhoneFriendId(f.odId);
      return;
    }

    // Friend detail remove
    if (action === "FRIEND_DETAIL_REMOVE") {
      const friendId = controller.getPhoneFriendId();
      if (!friendId || friendId === "npc-rafsby") return;
      const allFriends = friendManager.getCachedFriends();
      const friend = allFriends.find(f => f.odId === friendId);
      if (!friend) return;
      pendingFriendRemoval = { odId: friend.odId, username: friend.username };
      friendRemoveBusy = true;
      void friendManager.removeFriend(friendId).then(() => {
        friendRemoveBusy = false;
        pendingFriendRemoval = null;
        controller.setPhoneFriendId(null);
        void friendManager.refreshAll();
      }).catch((err) => {
        friendRemoveBusy = false;
        console.warn("[friends] remove failed:", err instanceof Error ? err.message : String(err));
      });
      return;
    }

    // Friend detail battle
    if (action === "FRIEND_DETAIL_BATTLE") {
      const friendId = controller.getPhoneFriendId();
      if (!friendId) return;
      // TODO: implement battle invite
      return;
    }

    if (action === "OPEN_FRIENDS") {
      // Let stateMachine handle phase transition (opens phone on friends tab)
      // Just reset friends state and refresh data here
      friendsPage = 0;
      pendingFriendRemoval = null;
      friendRemoveBusy = false;
      void friendManager.refreshAll();
      friendsPollTimer = 0; // force immediate poll cycle
      // Don't return — let stateMachine also process this action
    }
    if (action === "ADD_FRIEND" && (controller.phase === "FRIENDS" || controller.phase === "ADD_FRIEND" || controller.phase === "PHONE")) {
      const username = friendInput.value.trim().toLowerCase();
      if (!username) {
        friendInput.focus();
        return;
      }
      friendInput.value = "";
      void friendManager.sendRequest(username).then(({ error }) => {
        if (error != null) {
          friendsError = error;
          friendsErrorTimer = 3;
        } else {
          friendsError = "REQUEST SENT!";
          friendsErrorTimer = 3;
          void friendManager.refreshAll();
        }
      }).catch((err) => console.warn("[friends] send request failed:", err instanceof Error ? err.message : String(err)));
      return;
    }
    // Refer actions are handled by stateMachine (opens refer page)
    // Share and copy actions trigger the actual sharing
    if (action === "PHONE_REFER_SHARE" || action === "PHONE_REFER_COPY") {
      // Actual share/copy is handled by immediateGestureCallbacks (preserves user gesture context).
      // This handler just ensures the action is consumed.
      return;
    }
    if (action === "FRIEND_BATTLE" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      if (!staminaService.canPlay()) {
        controller.pendingGameplayIntent = { kind: "versus_return", returnPhase: "FRIENDS" };
        void adService.showAd().then(() => {
          void controller.restoreStaminaFromAd();
        }).catch((err) => console.warn("[ad] showAd failed:", err instanceof Error ? err.message : String(err)));
        return;
      }
      const idx = input.consumeFriendBattleIndex();
      if (idx == null) return;
      const friends = friendManager.getCachedFriends();
      const pageStart = friendsPage * 5;
      const actualIdx = pageStart + idx;
      if (actualIdx >= friends.length) return;
      if (!multiplayerBackendAvailable) {
        startBotMatch();
        return;
      }
      const friend = friends[actualIdx];
      controller.setVersusError("");
      // Fetch open lobbies (same query as lobby browser) and join friend's lobby if it exists
      void (async () => {
        try {
          const result = await lobbyManager.fetchOpenLobbies("custom", {
            fallbackMessage: "FAILED TO FIND LOBBY",
            networkMessage: "NETWORK FAILED TO FIND LOBBY"
          });
          if (result.error != null) {
            controller.setVersusError(result.error);
            return;
          }
          const friendLobby = result.lobbies.find((l) => l.hostId === friend.odId);
          if (friendLobby != null) {
            const { error } = await lobbyManager.joinExistingLobby(friendLobby.id);
            if (error != null) {
              controller.setVersusError(error);
              return;
            }
            controller.phase = "VERSUS_LOBBY";
            lobbyBrowserError = "";
            subscribeLobbyRealtime(friendLobby.id);
            void refreshOpenLobbies();
            return;
          }
          // No lobby from friend yet â€" create one and wait for them
          const { lobbyId, error } = await lobbyManager.createAndJoinLobby();
          if (error != null || lobbyId == null) {
            controller.setVersusError(error ?? "FAILED TO CREATE LOBBY");
            return;
          }
          controller.phase = "VERSUS_LOBBY";
          lobbyBrowserError = "";
          subscribeLobbyRealtime(lobbyId);
          void refreshOpenLobbies();
        } catch {
          controller.setVersusError("FAILED TO FIND LOBBY");
        }
      })();
      return;
    }
    if (action === "FRIEND_ACCEPT" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      const idx = input.consumeFriendAcceptIndex();
      if (idx == null) return;
      const pending = friendManager.getCachedPendingRequests();
      if (idx >= pending.length) return;
      void friendManager.acceptRequest(pending[idx].odId).then(() => {
        void friendManager.refreshAll();
      }).catch((err) => console.warn("[friends] accept request failed:", err instanceof Error ? err.message : String(err)));
      return;
    }
    if (action === "FRIEND_DECLINE" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      const idx = input.consumeFriendDeclineIndex();
      if (idx == null) return;
      const pending = friendManager.getCachedPendingRequests();
      if (idx >= pending.length) return;
      void friendManager.declineRequest(pending[idx].odId).then(() => {
        void friendManager.refreshAll();
      }).catch((err) => console.warn("[friends] decline request failed:", err instanceof Error ? err.message : String(err)));
      return;
    }
    if (action === "FRIEND_REMOVE" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      const idx = input.consumeFriendRemoveIndex();
      if (idx == null) return;
      const friends = friendManager.getCachedFriends();
      const pageStart = friendsPage * 5;
      const actualIdx = pageStart + idx;
      if (actualIdx >= friends.length) return;
      const target = friends[actualIdx];
      pendingFriendRemoval = { odId: target.odId, username: target.username };
      friendRemovePrompt = FRIEND_REMOVE_PROMPTS[friendRemovePromptCursor % FRIEND_REMOVE_PROMPTS.length];
      friendRemovePromptCursor += 1;
      return;
    }
    if (action === "FRIEND_REMOVE_CANCEL" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      if (!friendRemoveBusy) {
        pendingFriendRemoval = null;
      }
      return;
    }
    if (action === "FRIEND_REMOVE_CONFIRM" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      if (friendRemoveBusy || pendingFriendRemoval == null) return;
      const removing = pendingFriendRemoval;
      const totalAfterRemove = Math.max(
        0,
        friendManager.getCachedFriends().filter((friend) => friend.odId !== removing.odId).length
      );
      friendRemoveBusy = true;
      void friendManager.removeFriend(removing.odId)
        .then(() => {
          void friendManager.refreshAll();
          const maxPage = Math.max(0, Math.ceil(totalAfterRemove / 5) - 1);
          if (friendsPage > maxPage) friendsPage = maxPage;
        })
        .catch(() => {
          friendsError = "REMOVE FAILED";
          friendsErrorTimer = 3;
        })
        .finally(() => {
          pendingFriendRemoval = null;
          friendRemoveBusy = false;
        });
      return;
    }
    if (action === "FRIENDS_PAGE_PREV" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      if (friendsPage > 0) friendsPage--;
      return;
    }
    if (action === "FRIENDS_PAGE_NEXT" && (controller.phase === "FRIENDS" || controller.phase === "PHONE")) {
      const friends = friendManager.getCachedFriends();
      const maxPage = Math.max(0, Math.ceil(friends.length / 5) - 1);
      if (friendsPage < maxPage) friendsPage++;
      return;
    }

    // â"€â"€ Profile settings â"€â"€
    if (action === "CHANGE_USERNAME") {
      usernamePickReturnPhase = "PROFILE_SETTINGS";
    }
    if (action === "TOGGLE_APPEAR_OFFLINE") {
      // The stateMachine toggles the flag and saves to localStorage;
      // sync the friendManager presence immediately
      setTimeout(() => {
        friendManager.setAppearOffline(isAppearOffline());
      }, 0);
    }

    // â"€â"€ Username pick â"€â"€
    const isPhoneEditUsername = controller.phase === "PHONE" && controller.getPhoneEditUsernameMode();
    if (action === "FOCUS_USERNAME_INPUT" && (controller.phase === "USERNAME_PICK" || isPhoneEditUsername)) {
      usernameInput.focus();
      return;
    }
    if (action === "CLAIM_USERNAME" && (controller.phase === "USERNAME_PICK" || isPhoneEditUsername)) {
      const username = usernameInput.value.trim().toLowerCase();
      if (username.length < 3) {
        controller.setVersusError("USERNAME TOO SHORT (MIN 3)");
        usernameInput.focus();
        return;
      }
      if (username.length > 16) {
        controller.setVersusError("USERNAME TOO LONG (MAX 16)");
        usernameInput.focus();
        return;
      }
      controller.setVersusError("");
      void authManager.setUsername(username).then(({ error }) => {
        if (error != null) {
          controller.setVersusError(error);
        } else {
          usernameInput.value = "";
          usernameInput.blur();
          // Apply pending referral on first username claim (new account)
          const userId = authManager.getState().user?.id;
          if (userId != null && userId !== "guest-local") {
            void referralService.applyReferral(userId);
          }
          if (isPhoneEditUsername) {
            controller.clearPhoneEditUsernameMode();
          } else if (usernamePickReturnPhase === "PROFILE_SETTINGS") {
            controller.phase = "PROFILE_SETTINGS";
            usernamePickReturnPhase = "TITLE";
          } else if (hasOnboardingBeenSeen()) {
            controller.phase = "TITLE";
          } else {
            controller.enterOnboarding();
          }
        }
      }).catch((err) => console.warn("[auth] set username failed:", err instanceof Error ? err.message : String(err)));
      return;
    }

    if (action === "ONBOARDING_SKIP") {
      markOnboardingSeen();
      // State machine already sets phase to TITLE
      return;
    }

    if (action === "VERSUS_EXIT" && controller.phase === "VERSUS_RESULT") {
      // Broadcast leave to opponent before cleaning up
      if (!isBotMatch && lobbyRealtime.isConnected()) {
        lobbyRealtime.broadcastRematchLeave();
      }
      endQuickmatchSearchSession();
      quickmatchAttemptGuard.invalidate();
      unsubscribeLobbyRealtime();
      void lobbyManager.closeLobby();
      cleanupVersusMatch();
      isBotMatch = false;
      activeBotIdentity = null;
      lastBotCountdownIdentity = null;
      lastBotResultRemoteDisplayName = null;
      botPlayer = null;
      versusResultData = null;
      rematchLocalWants = false;
      rematchRemoteWants = false;
      rematchRemoteLeft = false;
      controller.phase = "TITLE";
      return;
    }

    if (action === "VERSUS_REMATCH" && controller.phase === "VERSUS_RESULT") {
      if (rematchLocalWants) return; // Already pressed
      if (rematchRemoteLeft) return; // Opponent already left

      rematchLocalWants = true;
      // Broadcast rematch intent to opponent
      lobbyRealtime.broadcastRematch(true);
      checkRematchBothReady();
      return;
    }

    if (action === "MAIN_MENU") {
      if (controller.phase === "VERSUS_LOBBY") {
        endQuickmatchSearchSession();
        quickmatchAttemptGuard.invalidate();
        unsubscribeLobbyRealtime();
        controller.setVersusBusy(false);
        void lobbyManager.leaveLobby();
        controller.phase = "TITLE";
        return;
      }
      if (controller.phase === "VERSUS_RESULT") {
        if (!isBotMatch && lobbyRealtime.isConnected()) {
          lobbyRealtime.broadcastRematchLeave();
        }
        endQuickmatchSearchSession();
        quickmatchAttemptGuard.invalidate();
        unsubscribeLobbyRealtime();
        void lobbyManager.closeLobby();
        cleanupVersusMatch();
        isBotMatch = false;
        activeBotIdentity = null;
        lastBotCountdownIdentity = null;
        lastBotResultRemoteDisplayName = null;
        botPlayer = null;
        versusResultData = null;
        rematchLocalWants = false;
        rematchRemoteWants = false;
        rematchRemoteLeft = false;
        controller.phase = "TITLE";
        return;
      }
      if (controller.phase === "VERSUS_MATCH") {
        endQuickmatchSearchSession();
        quickmatchAttemptGuard.invalidate();
        if (versusCtrl != null) {
          versusOptionsOpen = false;
          versusCtrl.forfeit();
          return;
        }
        cleanupVersusMatch();
        isBotMatch = false;
        activeBotIdentity = null;
        lastBotCountdownIdentity = null;
        lastBotResultRemoteDisplayName = null;
        botPlayer = null;
        controller.phase = "TITLE";
        return;
      }
      if (controller.phase === "FRIENDS") {
        controller.phase = "TITLE";
        return;
      }
      if (controller.phase === "VERSUS_COUNTDOWN") {
        endQuickmatchSearchSession();
        quickmatchAttemptGuard.invalidate();
        countdownActive = false;
        botMatchPending = null;
        isBotMatch = false;
        activeBotIdentity = null;
        lastBotCountdownIdentity = null;
        lastBotResultRemoteDisplayName = null;
        botPlayer = null;
        controller.phase = "TITLE";
        return;
      }
    }
  }

  function buildLobbyRenderData(): VersusLobbyRenderData | null {
    const lobby = lobbyManager.getCurrentLobby();
    if (lobby == null) return null;
    const localId = lobbyManager.getLocalUserId();
    return {
      lobbyCode: lobby.id,
      players: lobby.players.map((p) => ({
        displayName: p.displayName,
        elo: p.elo,
        ready: p.ready,
        isLocal: p.userId === localId,
        equippedSkin: p.equippedSkin,
        level: p.level
      })),
      isHost: lobbyManager.isHost(),
      localReady: lobbyManager.getLocalPlayer()?.ready ?? false,
      error: versusMenuActionState.error,
      chatMessages: lobbyChatMessages,
      connected: lobbyRealtimeConnected
    };
  }

  const step = 1 / 60;
  let lastTs = performance.now() / 1000;
  let lastRenderTs = 0;
  let accumulator = 0;
  let prevPhase: string = controller.phase;
  let prevPhaseForInput: string = controller.phase;
  let usernamePickEnterMs = 0;
  let prevLeaderboardTab: LeaderboardTab = controller.getLeaderboardTab();
  let prevPhoneTab = controller.getPhoneTab();
  let lastUiPhase: string = controller.phase;
  let uiTransitionPending = false;
  let lastRadialSnapshot: RadialMenuState | null = null;
  let radialSelectedPowerupId: string | null = null;
  let prevHoveredRect: { x: number; y: number; w: number; h: number } | null = null;
  let currentLoadingAction: string | null = null;
  let iapPurchaseState: IapPurchaseState = {
    phase: "idle", packIndex: -1, gems: 0, priceString: "", label: "", message: "", messageTimer: 0,
  };

  const frame = (ts: number): void => {
    const now = ts / 1000;
    const dt = Math.min(0.1, now - lastTs);
    lastTs = now;
    accumulator += dt;

    // Decay versus error timer
    if (versusMenuActionState.errorTimerSec > 0) {
      versusMenuActionState.errorTimerSec -= dt;
      if (versusMenuActionState.errorTimerSec <= 0) {
        versusMenuActionState.errorTimerSec = 0;
        versusMenuActionState.error = "";
        if (versusMenuActionState.statusLine === "RETRYING...") {
          versusMenuActionState.statusLine = "";
        }
      }
    }

    // Auto-dismiss IAP purchase messages
    if (iapPurchaseState.phase === "success" || iapPurchaseState.phase === "error") {
      iapPurchaseState.messageTimer -= dt;
      if (iapPurchaseState.messageTimer <= 0) {
        iapPurchaseState = {
          phase: "idle", packIndex: -1, gems: 0, priceString: "", label: "", message: "", messageTimer: 0,
        };
      }
    }

    const observedDailyLoginUtc = currentUtcDateString();
    if (observedDailyLoginUtc !== lastObservedDailyLoginUtc) {
      lastObservedDailyLoginUtc = observedDailyLoginUtc;
      const userId = authManager.getState().user?.id;
      if (userId != null && userId !== "guest-local") {
        void refreshDailyLoginStatus(userId, { autoOpen: true });
      }
    }

    // Countdown tick
    if (countdownActive && controller.phase === "VERSUS_COUNTDOWN") {
      countdownRemaining -= dt;
      if (countdownRemaining <= -0.5) {
        // -0.5 gives a brief "GO!" display before transitioning
        if (isBotMatch) {
          handleBotMatchStart();
        } else {
          void handleCountdownEnd();
        }
      }
    }

    // Bot search timeout â€" if quickmatch hasn't found an opponent, spawn bot
    if (quickmatchSearchActive && (controller.phase === "TITLE" || controller.phase === "VERSUS_LOBBY")) {
      botSearchTimer += dt;
      if (shouldFallbackToBot({
        searchPending: quickmatchSearchActive,
        elapsedSec: botSearchTimer,
        timeoutSec: BOT_SEARCH_TIMEOUT_SEC
      })) {
        endQuickmatchSearchSession();
        if (controller.phase === "VERSUS_LOBBY") {
          unsubscribeLobbyRealtime();
          void lobbyManager.leaveLobby();
        }
        startBotMatch();
      }
    }

    // If quickmatch created a lobby but no opponent ever joins, fallback to bot.
    if (controller.phase === "VERSUS_LOBBY" && controller.getViewModel().versusBusy) {
      const lobby = lobbyManager.getCurrentLobby();
      if (lobby != null && lobby.players.length < 2) {
        lobbyWaitForOpponentSec += dt;
        if (lobbyWaitForOpponentSec >= BOT_SEARCH_TIMEOUT_SEC) {
          endQuickmatchSearchSession();
          unsubscribeLobbyRealtime();
          void lobbyManager.leaveLobby();
          startBotMatch();
        }
      } else {
        lobbyWaitForOpponentSec = 0;
      }
    } else {
      lobbyWaitForOpponentSec = 0;
    }

    while (true) {
      const nextVolume = input.consumeMusicVolumeChange();
      if (nextVolume == null) {
        break;
      }
      audio.setMusicVolume(nextVolume);
    }

    while (true) {
      const nextSfxVolume = input.consumeSfxVolumeChange();
      if (nextSfxVolume == null) {
        break;
      }
      audio.setSfxVolume(nextSfxVolume);
    }

    while (accumulator >= step) {
      if (versusCtrl != null && controller.phase === "VERSUS_MATCH") {
        // During a versus match, the VersusController drives both controllers.
        // Input ALWAYS flows through versusCtrl.
        if (isBotMatch && botPlayer != null) {
          const mirrorRun = versusCtrl.mirrorController.run;
          const localRun = versusCtrl.localController.run;
          const mirrorBoard = mirrorRun?.board ?? null;
          const grooveFull = (mirrorRun?.groove?.groove ?? 0) >= 1;
          const botScore = mirrorRun?.totalScore ?? 0;
          const localScore = localRun?.totalScore ?? 0;
          const pendingTrap = botPlayer.input.updateBot(
            step,
            mirrorBoard,
            grooveFull,
            botScore - localScore,
            {
              localBoard: localRun?.board ?? null,
              localFogActive: versusCtrl.isFogActive(),
              localCash: localRun?.cashBalance ?? 0,
              localDoomSec: localRun?.doomClockSec ?? 0,
              localGroovePct: localRun?.groove?.groove ?? 0,
              localGrooveActive: localRun?.groove?.active ?? false,
              localGrooveArmed: localRun?.groove?.armed ?? false,
              remoteCash: mirrorRun?.cashBalance ?? 0
            }
          );
          if (pendingTrap != null) {
            versusCtrl.castRemoteBotTrap(pendingTrap);
          }
          versusCtrl.localController.setBotDecisionDiagnostics(botPlayer.getDecisionDiagnostics());
          botPlayer.updateFakeStats(step);
        } else {
          versusCtrl.localController.setBotDecisionDiagnostics(null);
        }
        versusCtrl.update(step, input);
      } else {
        // Handle rejoin cancel before normal update
        if (rejoinOverlayVisible && input.hasAction("REJOIN_CANCEL")) {
          cancelRejoin();
        }
        controller.setBotDecisionDiagnostics(null);
        controller.update(step, input);
      }
      accumulator -= step;
    }

    // Periodically save run state snapshots for versus rejoin (every 2 seconds)
    if (versusCtrl != null && controller.phase === "VERSUS_MATCH") {
      matchSnapshotTimer += dt;
      if (matchSnapshotTimer >= 2) {
        matchSnapshotTimer = 0;
        const localRun = versusCtrl.localController.run;
        const mirrorRun = versusCtrl.mirrorController.run;
        if (localRun != null && mirrorRun != null) {
          saveMatchSnapshot(localRun, mirrorRun);
        }
      }
    } else {
      matchSnapshotTimer = 0;
    }

    // â"€â"€ Economy: detect phase transitions â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
    if (controller.phase === "AD_GATE" && staminaService.canPlay()) {
      controller.resumeAfterStaminaGateResolved();
    }
    const currentPhase = controller.phase;
    if (prevPhase === "VERSUS_MATCH" && currentPhase !== "VERSUS_MATCH") {
      versusOptionsOpen = false;
    }
    if (currentPhase !== lastUiPhase) {
      input.setUiTransition(true);
      uiTransitionPending = true;
      lastUiPhase = currentPhase;
    }
    if (currentPhase === "RUN_GAMEOVER" && prevPhase !== "RUN_GAMEOVER") {
      // Snapshot XP state before this run's XP is awarded
      const profileBeforeXp = authManager.getState().profile;
      const totalXpBefore = profileBeforeXp?.totalXp ?? 0;
      const xpInfoBefore = levelFromTotalXp(totalXpBefore);
      const xpProgressBefore = xpInfoBefore.xpNeeded > 0
        ? xpInfoBefore.xpIntoLevel / xpInfoBefore.xpNeeded
        : 0;
      controller.setXpBeforeRun(xpProgressBefore);

      const coinsEarned = controller.calculateRunCoins();
      if (coinsEarned > 0) {
        // Always credit locally first for immediate UI feedback
        const localBal = creditCoinsLocal(coinsEarned);
        controller.onCoinsEarned(localBal);

        const userId = authManager.getState().user?.id;
        if (userId != null && userId !== "guest-local" && networkManager.isOnline()) {
          // Also sync to server â€" server response will reconcile the balance
          void creditCoins(userId, coinsEarned, "solo_run").then((newBal) => {
            if (newBal != null) controller.onCoinsEarned(newBal);
          }).catch((err) => console.warn("[economy] credit coins failed:", err instanceof Error ? err.message : String(err)));
        }
      }

      // Award XP for solo run
      const viewModel = controller.getViewModel();
      const soloEligible = isSoloRunXpEligible({
        bestLevelReached: viewModel.latestRunLevel,
        durationSec: viewModel.run?.beat.songTimeSec ?? 0,
      });
      if (soloEligible) {
        const xpEarned = soloRunXp({
          bestLevelReached: viewModel.latestRunLevel,
          totalScore: viewModel.latestRunScore,
        });
        // Compute level-up before the async award (pure function)
        const xpResult = addXp(totalXpBefore, xpEarned);
        if (xpResult.levelsGained > 0) {
          controller.setLevelUpTriggered(true, xpResult.level);
          const unlocked = modesNewlyUnlocked(xpInfoBefore.level, xpResult.level);
          if (unlocked.length > 0) {
            controller.setNewlyUnlockedModes(unlocked);
          }
        }
        void awardPersistentXp(xpEarned);
      }
    }
    if (prevPhase === "RUN_GAMEOVER" && currentPhase !== "RUN_GAMEOVER") {
      controller.resetCoinsEarnedThisRun();
    }

    const currentLeaderboardTab = controller.getLeaderboardTab();
    const currentPhoneTab = controller.getPhoneTab();
    const phoneLeaderboardVisible = currentPhase === "PHONE" && currentPhoneTab === "highscores";
    const prevPhoneLeaderboardVisible = prevPhase === "PHONE" && prevPhoneTab === "highscores";
    const leaderboardViewChanged = (
      currentPhase === "LEADERBOARD" && (
        prevPhase !== "LEADERBOARD" || currentLeaderboardTab !== prevLeaderboardTab
      )
    ) || (
      phoneLeaderboardVisible && (
        !prevPhoneLeaderboardVisible || currentLeaderboardTab !== prevLeaderboardTab
      )
    );
    if (leaderboardViewChanged) {
      if (currentLeaderboardTab === "solo") {
        void controller.refreshLeaderboard();
      } else {
        void refreshVersusLeaderboard();
      }
    }

    // Fetch ranked data when entering profile page
    if (currentPhase === "PROFILE" && prevPhase !== "PROFILE") {
      void refreshVersusLeaderboard();
    }

    // Loading state tracking
    if (currentPhase !== prevPhase && !currentLoadingAction?.startsWith("auth_")) {
      currentLoadingAction = null;
    }
    // Auth loading: derive from authManager.loading while on AUTH_GATE
    if (currentPhase === "AUTH_GATE" && authManager.getState().loading) {
      if (currentLoadingAction == null || !currentLoadingAction.startsWith("auth_")) {
        currentLoadingAction = "auth_google"; // generic auth spinner
      }
    }

    // Check for versus match rejoin when phase first becomes TITLE
    if (!rejoinChecked && currentPhase === "TITLE" && !rejoinOverlayVisible && authManager.getState().user != null) {
      rejoinChecked = true;
      tryRejoinActiveMatch();
    }

    prevPhase = currentPhase;
    prevLeaderboardTab = currentLeaderboardTab;
    prevPhoneTab = currentPhoneTab;
    const configuredRenderFpsCap = Math.max(30, lastPerformanceSettings.fpsCap);
    const hiddenTabRenderFpsCap = 5;
    const effectiveRenderFpsCap = document.hidden
      ? Math.min(configuredRenderFpsCap, hiddenTabRenderFpsCap)
      : configuredRenderFpsCap;
    const minRenderIntervalSec = 1 / Math.max(1, effectiveRenderFpsCap);
    const shouldRender = lastRenderTs <= 0 || now - lastRenderTs >= minRenderIntervalSec;

    const view = controller.getViewModel() as RenderModel;
    if (currentPhase !== "FRIENDS" && currentPhase !== "ADD_FRIEND") {
      pendingFriendRemoval = null;
      friendRemoveBusy = false;
    }

    // Decay friends error timer
    if (friendsErrorTimer > 0) {
      friendsErrorTimer -= dt;
      if (friendsErrorTimer <= 0) {
        friendsErrorTimer = 0;
        friendsError = "";
      }
    }

    // Poll friends data periodically
    friendsPollTimer -= dt;
    if (friendsPollTimer <= 0) {
      const interval = (currentPhase === "FRIENDS" || currentPhase === "ADD_FRIEND")
        ? FRIENDS_POLL_ACTIVE
        : FRIENDS_POLL_BG;
      friendsPollTimer = interval;
      void friendManager.refreshAll();
    }

    // Poll open lobbies while browsing lobby page
    if (currentPhase === "VERSUS_LOBBY" && lobbyManager.getCurrentLobby() == null && !view.versusBusy) {
      lobbyBrowsePollTimer -= dt;
      if (lobbyBrowsePollTimer <= 0) {
        lobbyBrowsePollTimer = 3;
        void refreshOpenLobbies();
      }
    } else {
      lobbyBrowsePollTimer = 0;
    }

    // Update friends online count
    controller.setFriendsOnlineCount(friendManager.getOnlineFriendCount());

    if (shouldRender) {
      // Overlay friends data onto view model
      const pendingFriendReqs = friendManager.getCachedPendingRequests();
      // Merge friend requests into message inbox as virtual messages
      const readFriendMsgIds = new Set<string>();
      messageService.mergeFriendRequests(pendingFriendReqs, readFriendMsgIds);
      // Badge count = unread messages (includes friend requests)
      controller.setUnreadPhoneCount(messageService.getUnreadCount());

      if (view.phase === "PHONE") {
        const allFriends = [{ ...RAFSBY_NPC }, ...friendManager.getCachedFriends()];
        const pageStart = friendsPage * 5;
        view.friendsList = allFriends.slice(pageStart, pageStart + 5);
        view.pendingRequests = pendingFriendReqs;
        view.sentRequests = friendManager.getCachedSentRequests();
        view.friendsPage = friendsPage;
        view.friendsTotalCount = allFriends.length;
        view.inboxMessages = messageService.getCachedInbox();
      }

      if (view.phase === "FRIENDS" || view.phase === "ADD_FRIEND" || view.phase === "PHONE") {
      const allFriends = [{ ...RAFSBY_NPC }, ...friendManager.getCachedFriends()];
      const pageStart = friendsPage * 5;
      view.friendsList = allFriends.slice(pageStart, pageStart + 5);
      view.pendingRequests = pendingFriendReqs;
      view.sentRequests = friendManager.getCachedSentRequests();
      view.friendsError = friendsError;
      view.friendRemoveConfirm = pendingFriendRemoval == null
        ? null
        : { username: pendingFriendRemoval.username, prompt: friendRemovePrompt };
      view.friendRemoveBusy = friendRemoveBusy;
      view.friendsPage = friendsPage;
        view.friendsTotalCount = allFriends.length;
      }

      view.dailyLoginStatus = dailyLoginStatus;
      view.dailyLoginLoading = dailyLoginLoading;
      view.dailyLoginClaimPending = dailyLoginClaimPending;

      // Overlay versus lobby data onto view model
      if (view.phase === "VERSUS_LOBBY") {
      view.versusLobby = buildLobbyRenderData();
      view.openLobbies = openLobbies.map((row) => ({ code: row.id, players: row.players.length }));
      view.lobbyBrowserError = lobbyBrowserError;
      view.lobbyLastRefreshMs = lobbyLastRefreshMs;
      view.lobbyJoinInputOpen = lobbyJoinInputOpen;
      }
      if (view.phase === "VERSUS_COUNTDOWN") {
      const profile = authManager.getState().profile;
      const localSkin = getCachedEquippedCharacter().id ?? "beast_starter";
      const localUsername = profile?.username ?? "PLAYER";
      const localLevel = profile?.level ?? 1;

      if (isBotMatch && botMatchPending != null) {
        lastBotCountdownIdentity = botMatchPending.botIdentity;
        view.versusCountdown = {
          secondsRemaining: countdownRemaining,
          players: [],
          localSkin,
          localUsername,
          localLevel,
          opponentSkin: botMatchPending.botIdentity.equippedSkin,
          opponentUsername: botMatchPending.botIdentity.username,
          opponentLevel: botMatchPending.botIdentity.level
        };
      } else {
        const lobby = lobbyManager.getCurrentLobby();
        const localId = lobbyManager.getLocalUserId();
        const remote = lobby?.players.find((p) => p.userId !== localId);
        view.versusCountdown = {
          secondsRemaining: countdownRemaining,
          players: (lobby?.players ?? []).map((p) => ({
            displayName: p.displayName,
            elo: p.elo
          })),
          localSkin,
          localUsername,
          localLevel,
          opponentSkin: remote?.equippedSkin ?? "beast_starter",
          opponentUsername: remote?.displayName ?? "OPPONENT",
          opponentLevel: remote?.level ?? 1
          };
        }
      }
      if (view.phase === "VERSUS_MATCH" && versusCtrl != null) {
      // Overlay the local controller's view data onto the main view
      // (the main controller has no run state during versus)
      const localView = versusCtrl.localController.getViewModel();
      applyVersusLocalViewOverlay(view, localView);

      // Build opponent data for the right panel
      const matchState = versusCtrl.getMatchState();
      const mirrorView = versusCtrl.mirrorController.getViewModel();
      view.versusMatch = {
        opponentBoard: mirrorView.run?.board ?? null,
        opponentRotationAnim: mirrorView.rotationAnim,
        opponentSwapAnim: mirrorView.swapAnim,
        opponentCollapseAnim: mirrorView.collapseAnim,
        opponentStats: matchState.opponentStats,
        syncHealth: matchState.syncHealth,
        syncMetrics: matchState.syncMetrics,
        trapsEnabled: matchState.settings.trapsEnabled,
        trapActionsEnabled: versusCtrl.areLocalTrapActionsEnabled(),
        trapCooldownRemainingSec: matchState.trapCooldownRemainingSec,
        disconnectCountdownSec: matchState.disconnectCountdownSec,
        suddenDeath: matchState.suddenDeath,
        localCash: localView.run?.cashBalance ?? 0,
        matchId: matchState.matchId,
        fogActive: versusCtrl.isFogActive(),
        fogRemainingSec: versusCtrl.getFogRemainingSec(),
        lastSentTrap: versusCtrl.getLastSentTrap()
        } satisfies VersusMatchRenderData;
      }
      view.versusOptionsOpen = view.phase === "VERSUS_MATCH" ? versusOptionsOpen : false;
      input.setVersusOptionsOpen(view.versusOptionsOpen);
      if (view.phase === "VERSUS_RESULT") {
        view.versusResult = versusResultData;
        // Provide current rating (updates after DB trigger refreshes profile — works for both bot and real matches)
        view.versusRatingAfter = authManager.getState().profile?.versusElo ?? null;
        view.rematchLocalWants = rematchLocalWants;
        view.rematchRemoteWants = rematchRemoteWants;
        view.rematchRemoteLeft = rematchRemoteLeft;
      }
      view.versusActionPhase = versusMenuActionState.phase;
      view.versusActionName = versusMenuActionState.activeAction;
      view.versusStatusLine = versusMenuActionState.statusLine;

      view.isOnline = networkManager.isOnline();
      view.loadingAction = currentLoadingAction;
      // IAP data for gem shop (native only)
      const iapAvailable = iapManager.isNativeIapAvailable();
      view.iapPacks = iapAvailable
        ? iapManager.getAvailablePackages().map(pkg => {
            const def = getIapPackByProductId(pkg.product.identifier);
            return {
              productId: pkg.product.identifier,
              gems: def?.gems ?? 0,
              priceString: pkg.product.priceString,
              label: def?.label ?? pkg.product.title,
              bestValue: def?.bestValue ?? false,
            };
          })
        : [];
      view.iapAvailable = iapAvailable;
      view.iapPurchaseState = iapPurchaseState;
      const walletStateForFrame = walletService.getState();
      const equippedIdForFrame = (() => {
        try {
          return localStorage.getItem("bitbeast.equipped.v1") ?? null;
        } catch {
          return null;
        }
      })();
      const delegatedTokenMapForFrame = walletStateForFrame.delegatedTokenMap;
      const delegatedTokenIdsForFrame = Object.keys(delegatedTokenMapForFrame).length > 0
        ? new Map(Object.entries(delegatedTokenMapForFrame))
        : null;
      const nftAvatarsForFrame = resolveAvatarList(
        walletStateForFrame.unlockedTokenIds,
        equippedIdForFrame,
        delegatedTokenIdsForFrame
      );
      controller.setProfileNftAvatarCount(nftAvatarsForFrame.length);
      // â"€â"€ ETH wallet / NFT avatar state â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
      view.walletAddress = walletService.getShortenedAddress();
      view.walletVerified = walletStateForFrame.isVerified;
      view.isBitbeastOwner = walletStateForFrame.isBitbeastOwner;
      controller.setIsBitbeastOwner(walletStateForFrame.isBitbeastOwner);
      view.walletSyncStatus = walletStateForFrame.syncStatus;
      view.walletLastSync = formatRelativeTime(walletStateForFrame.lastSyncAt);
      view.walletConnecting = walletConnecting;
      view.nftSyncing = nftSyncing;
      view.unlockedAvatarTokenIds = walletStateForFrame.unlockedTokenIds;
      view.nftAvatars = nftAvatarsForFrame;
      view.nftSyncAge = formatSyncAge(walletStateForFrame.lastSyncAt);
      view.nftCacheStale = isCacheStale(walletStateForFrame.lastSyncAt);
      view.delegatedVaults = walletStateForFrame.delegatedVaults;
      // â"€â"€ Solana wallet state â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
      const solState = solanaService.getState();
      view.solWalletAddress = solState.address ? `${solState.address.slice(0, 4)}...${solState.address.slice(-4)}` : null;
      view.solWalletConnected = solState.isConnected;
      view.solTokenBalances = solState.tokenBalances.map((t) => ({ mint: t.mint, symbol: t.symbol, balance: t.balance, usdValue: t.usdValue }));
      view.solBalanceLoading = solState.balanceLoading;
      // â"€â"€ Token shop state â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
      const shopState = tokenShopService.getState();
      const activeToken = shopState.activeToken;
      view.tokenShopItems = shopState.items.map((item) => ({
        id: item.id,
        displayName: item.displayName,
        usdPrice: item.usdPrice,
        tokenPrice: shopState.itemPrices[item.id] ?? null,
        gemAmount: item.gemAmount,
        itemType: item.itemType,
        tokenSymbol: activeToken?.symbol ?? null,
      }));
      view.tokenPurchaseState = shopState.purchaseState;
      view.tokenPurchaseError = shopState.purchaseError;
      view.tokenVerifyAttemptCount = shopState.verifyAttemptCount ?? 0;
      view.tokenSelectedItemName = shopState.selectedItem?.displayName ?? null;
      view.tokenSelectedAmount = shopState.tokenPrice != null && shopState.selectedToken != null
        ? `${shopState.tokenPrice.toFixed(2)} ${shopState.selectedToken.symbol}`
        : null;
      view.hasWhitelistedTokens = whitelistedTokensExist;
      view.activeTokenSymbol = activeToken?.symbol ?? null;
      // â"€â"€ Referral stats (cached, refreshed on profile open) â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
      view.referralStats = cachedReferralStats;
      // â"€â"€ PWA install prompt â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
      view.showInstallApp = pwaInstallPrompt.shouldShow();
      view.pushEnabled = cachedPushEnabled;
      view.pushSupported = cachedPushSupported;
      // â"€â"€ Radial menu state from input hold â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
      const radialHold = input.getRadialMenuState();
      if (radialHold != null && radialHold.holdReached) {
        const isVersus = view.phase === "VERSUS_MATCH";
        let items;
        if (radialHold.kind === "powerup") {
          const inv = view.run?.powerupInventory;
          const cash = Math.max(0, Math.floor(view.run?.cashBalance ?? 0));
          items = createPowerupRadialItems(inv, cash);
        } else if (isVersus && view.versusMatch != null) {
          items = createTrapRadialItems(
            view.versusMatch.localCash,
            view.versusMatch.trapCooldownRemainingSec,
            view.versusMatch.trapActionsEnabled
          );
        } else {
          items = createTrapRadialItems(0, 99, false);
        }
        const highlighted = resolveHighlightedIndex(
          radialHold.hubX, radialHold.hubY,
          radialHold.pointerX, radialHold.pointerY,
          items
        );
        lastRadialSnapshot = {
          kind: radialHold.kind,
          active: true,
          hubX: radialHold.hubX,
          hubY: radialHold.hubY,
          items,
          highlightedIndex: highlighted,
          openProgress: Math.min(1, (radialHold.holdElapsedMs - RADIAL_HOLD_THRESHOLD_MS) / 150),
          holdStartMs: 0,
          pointerId: 0
        };
        // Track which powerup the user is hovering for persistence after release
        if (highlighted != null && radialHold.kind === "powerup") {
          radialSelectedPowerupId = items[highlighted].id;
        }
        view.radialMenu = lastRadialSnapshot;
      } else if (lastRadialSnapshot != null && lastRadialSnapshot.kind === "powerup" &&
                 radialSelectedPowerupId != null &&
                 (view.armedPowerup != null || view.powerupExecuting || input.hasPendingRadialRelease())) {
        // Keep radial menu visible while powerup is armed, executing, or release is being processed
        const activeIdx = lastRadialSnapshot.items.findIndex((it) => it.id === (view.armedPowerup ?? radialSelectedPowerupId));
        view.radialMenu = { ...lastRadialSnapshot, active: true, highlightedIndex: activeIdx >= 0 ? activeIdx : lastRadialSnapshot.highlightedIndex, openProgress: 1 };
      } else {
        view.radialMenu = null;
        lastRadialSnapshot = null;
        radialSelectedPowerupId = null;
      }
      renderer.setPressedButton(input.getLastPressedRect());
      renderer.setHoverPosition(input.getHoverPosition());
      renderer.setLeaderboardScrollGesture(input.consumeLeaderboardScrollGesture());
      // Play hover SFX on rect transition — only on title screen (buildings)
      const curHover = input.getHoveredRect();
      const hoverSfxPhases = new Set(["TITLE"]);
      if (curHover != null && hoverSfxPhases.has(currentPhase) && (prevHoveredRect == null ||
          curHover.x !== prevHoveredRect.x || curHover.y !== prevHoveredRect.y)) {
        void audio.playSfx("menu_hover");
      }
      prevHoveredRect = curHover;
      const uiRects = renderer.draw(view);
      if (view.gemBurstPending) controller.consumeGemBurst();

      // Draw rejoin overlay on top of everything
      if (rejoinOverlayVisible) {
        const ctx = renderer.getContext();
        const cw = ctx.canvas.width;
        const ch = ctx.canvas.height;
        ctx.fillStyle = "rgba(0,0,0,0.85)";
        ctx.fillRect(0, 0, cw, ch);
        ctx.fillStyle = "#64d8ff";
        ctx.font = "bold 14px 'Press Start 2P', monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("RECONNECTING...", cw / 2, ch / 2 - 30);
        ctx.fillStyle = "#aabbcc";
        ctx.font = "10px 'Press Start 2P', monospace";
        ctx.fillText(`${Math.ceil(rejoinTimeoutSec)}s`, cw / 2, ch / 2 + 5);
        // Cancel button
        const btnW = 100;
        const btnH = 28;
        const btnX = cw / 2 - btnW / 2;
        const btnY = ch / 2 + 30;
        ctx.fillStyle = "rgba(255,100,100,0.2)";
        ctx.fillRect(btnX, btnY, btnW, btnH);
        ctx.strokeStyle = "#ff6b6b";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(btnX, btnY, btnW, btnH);
        ctx.fillStyle = "#ff6b6b";
        ctx.font = "bold 8px 'Press Start 2P', monospace";
        ctx.fillText("CANCEL", cw / 2, btnY + btnH / 2);
        uiRects.rejoinCancel = { x: btnX, y: btnY, w: btnW, h: btnH };
      }

      input.setUiRects(uiRects);
      latestUiRects = uiRects;
      if (uiTransitionPending) {
        input.setUiTransition(false);
        uiTransitionPending = false;
      }
      lastRenderTs = now;
    }

    // Enable lowpass filter on menu/lobby screens, disable during gameplay
    const isGameplay = currentPhase === "RUN_PLAYABLE" || currentPhase === "RUN_RESOLVING"
      || currentPhase === "RUN_LEVELUP" || currentPhase === "VERSUS_MATCH";
    audio.setLowpass(!isGameplay);

    // Groove audio: ambience send follows groove fill, bass boost when groove drops
    const groove = (currentPhase === "VERSUS_MATCH" && versusCtrl != null
      ? versusCtrl.localController.run?.groove
      : view.run?.groove) ?? null;
    if (groove != null) {
      if (groove.active) {
        audio.setGrooveBuild(0);
        audio.setGrooveBassBoost(true);
      } else {
        audio.setGrooveBuild(Math.max(0, Math.min(1, groove.groove)));
        audio.setGrooveBassBoost(false);
      }
    } else {
      audio.setGrooveBuild(0);
      audio.setGrooveBassBoost(false);
    }

    authOverlay.syncPhase(currentPhase);

    positionOverlayInput(usernameInput, latestUiRects.usernameInput, 1);
    positionOverlayInput(friendInput, latestUiRects.addFriendInput, 1);
    positionOverlayInput(lobbyCodeInput, latestUiRects.lobbyCodeInput, 1);
    positionOverlayInput(lobbyChatInput, latestUiRects.lobbyChatInput, 1);

    // Show/hide username input overlay (delay during phone slide-up animation)
    if (currentPhase === "USERNAME_PICK" && prevPhaseForInput !== "USERNAME_PICK") {
      usernamePickEnterMs = performance.now();
    }
    prevPhaseForInput = currentPhase;
    const usernameSlideComplete = currentPhase !== "USERNAME_PICK" || (performance.now() - usernamePickEnterMs) > 300;
    const phoneEditUsername = currentPhase === "PHONE" && view.phoneEditUsernameMode;
    const showUsernameInput = (currentPhase === "USERNAME_PICK" && usernameSlideComplete) || phoneEditUsername;
    usernameInput.style.display = showUsernameInput ? "block" : "none";
    if (!showUsernameInput && usernameInput === document.activeElement) {
      usernameInput.blur();
    }

    // Show/hide friend input overlay
    const phoneAddMode = currentPhase === "PHONE" && view.phoneAddFriendMode;
    const showFriendInput = currentPhase === "ADD_FRIEND" || phoneAddMode;
    friendInput.style.display = showFriendInput ? "block" : "none";
    if (phoneAddMode) {
      positionOverlayInput(friendInput, latestUiRects.phoneAddFriendInput, 1);
    }
    if (!showFriendInput && friendInput === document.activeElement) {
      friendInput.blur();
    }

    const hasCurrentLobby = lobbyManager.getCurrentLobby() != null;
    const showLobbyCodeInput = currentPhase === "VERSUS_LOBBY" && !hasCurrentLobby && !view.versusBusy && lobbyJoinInputOpen;
    lobbyCodeInput.style.display = showLobbyCodeInput ? "block" : "none";
    if (!showLobbyCodeInput && lobbyCodeInput === document.activeElement) {
      lobbyCodeInput.blur();
    }

    const showLobbyChatInput = currentPhase === "VERSUS_LOBBY" && hasCurrentLobby && !view.versusBusy;
    lobbyChatInput.style.display = showLobbyChatInput ? "block" : "none";
    if (!showLobbyChatInput && lobbyChatInput === document.activeElement) {
      lobbyChatInput.blur();
    }

    const musicNow = audio.isEnabled();
    const sfxNow = audio.isSfxEnabled();
    const musicVolumeNow = audio.getMusicVolume();
    const sfxVolumeNow = audio.getSfxVolume();
    const hapticsNow = hapticManager.isEnabled();
    const performanceNow = controller.getPerformanceSettings();
    const debugSettingsNow = controller.getDebugSettings();
    const performanceChanged = !samePerformanceSettings(performanceNow, lastPerformanceSettings);
    const debugSettingsChanged = !sameDebugSettings(debugSettingsNow, lastDebugSettings);
    if (debugSettingsChanged) {
      applyDebugSettingsToControllers(debugSettingsNow, "runtime_sync");
      void saveDebugSettings(debugSettingsNow, {
        userId: authManager.getState().user?.id ?? null,
        canUseDebugSettings: currentDebugAccess.canUseDebugSettings,
        saveToAccount: authManager.hasBackend()
          ? (userId: string, nextSettings: DebugSettings) => authManager.saveAccountDebugSettings(userId, nextSettings)
          : undefined
      }).then((result) => {
        lastDebugSettings = result.settings;
        applyDebugSettingsToControllers(result.settings, "persistence_sync");
      });
    }
    if (
      musicNow !== lastMusicState
      || sfxNow !== lastSfxState
      || musicVolumeNow !== lastMusicVolume
      || sfxVolumeNow !== lastSfxVolume
      || hapticsNow !== lastHapticsState
      || performanceChanged
      || debugSettingsChanged
    ) {
      saveSettings({
        musicEnabled: musicNow,
        sfxEnabled: sfxNow,
        hapticsEnabled: hapticsNow,
        musicVolume: musicVolumeNow,
        sfxVolume: sfxVolumeNow,
        performance: performanceNow,
        debug: debugSettingsNow
      });
      lastMusicState = musicNow;
      lastSfxState = sfxNow;
      lastMusicVolume = musicVolumeNow;
      lastSfxVolume = sfxVolumeNow;
      lastHapticsState = hapticsNow;
      lastPerformanceSettings = performanceNow;
      lastDebugSettings = debugSettingsNow;
    }

    requestAnimationFrame(frame);
  };

  requestAnimationFrame(frame);
}

if (!import.meta.env.VITEST) {
  void bootstrap();
}
